import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  Grid,
  GridItem,
  HStack,
  FormErrorMessage,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Spinner,
  Link,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  UnorderedList,
  ListItem,
} from "@chakra-ui/react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DataTable from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faSyringe,
  faTable,
  faTrash,
  faCalculator,
  faListOl,
  faGears,
  faVial,
  faCalendar,
  faCapsules,
  faPrint,
  faFileUpload,
  faEdit,
  faTag,
  faPlus,
  faCheckCircle,
  faCheckDouble,
  faCircleCheck,
  faArrowRotateBackward,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import CustomDatePicker from "./CustomDatePicker";
import * as IconButton from "../themes/IconButtonSet";
import { useDropzone } from "react-dropzone";
import { StandardPageTitle, StandardHeader } from "../themes/StandardHeader";

const APPROVED_STATUSES = ["Approved", "Confirmed", "Completed"];
const API_HOST = process.env.REACT_APP_API_URL || "";
const BASE_URL = process.env.REACT_APP_BASE_URL || "";

// Custom styles for DataTable
const customStyles = {
  header: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "18px",
      fontWeight: "bold",
    },
  },
  headCells: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "16px",
      fontWeight: "semibold",
      padding: "10px",
    },
  },
  cells: {
    style: {
      padding: "10px",
      fontSize: "14px",
    },
  },
  rows: {
    style: {
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
  },
};

// ฟังก์ชันแปลงสถานะเป็นข้อความไทย
const getStatusText = (status) => {
  switch (status) {
    case "Draft":
      return "ร่างใบเบิก";
    case "Pending":
      return "รออนุมัติ";
    case "Approved":
      return "รอรับเข้าคลัง";
    case "Completed":
      return "รับเข้าคลังแล้ว";
    case "Cancelled":
      return "ยกเลิกใบเบิก";
    case "ReceiveRejected":
      return "คลังปลายทางปฏิเสธการรับ";
    default:
      return status || "";
  }
};

// Printable Requisition Form Component
const PrintableRequisitionForm = ({ formData, parentWarehouses, warehouses, vaccineOptions }) => {
  console.log("PrintableRequisitionForm rendered with formData:", formData);
  console.log("Parent Warehouses in Printable:", parentWarehouses);
  console.log("Warehouses in Printable:", warehouses);

  const getWarehouseName = (warehouseId, isParent) => {
    if (isParent) {
      const warehouse = parentWarehouses.find((w) => w.WarehouseID === warehouseId);
      return warehouse ? warehouse.WarehouseName : "-";
    }
    const warehouse = warehouses.find((w) => w.WarehouseID === warehouseId);
    return warehouse ? warehouse.WarehouseName : "-";
  };

  return (
    <Box
      style={{
        display: "none",
        position: "absolute",
        top: "-9999px",
        left: "-9999px",
        width: "100%",
        padding: "40px",
        backgroundColor: "white",
        fontFamily: "Arial, sans-serif",
      }}
      className="printable"
    >
      <style>
        {`
          @media print {
            .printable {
              display: block !important;
              position: static !important;
              width: 100% !important;
              height: auto !important;
            }
            body * {
              visibility: hidden;
            }
            .printable, .printable * {
              visibility: visible;
            }
          }
        `}
      </style>
      <Box textAlign="center" mb={8}>
        <Heading as="h1" size="xl">
          ใบเบิกวัคซีน
        </Heading>
      </Box>
      <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={8}>
        <Box>
          <Text fontWeight="bold">เลขที่เอกสาร:</Text>
          <Text>{formData.TransactionID || "-"}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">วันที่ขอเบิก:</Text>
          <Text>
            {formData.RequestDate
              ? formData.RequestDate.toLocaleDateString("th-TH", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : "-"}
          </Text>
        </Box>
        <Box>
          <Text fontWeight="bold">คลังต้นทาง:</Text>
          <Text>{getWarehouseName(formData.FromWarehouseID, true) || "-"}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">คลังปลายทาง:</Text>
          <Text>{getWarehouseName(formData.ToWarehouseID, false) || "-"}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">ประเภทงบประมาณ:</Text>
          <Text>
            {formData.BudgetType === "Prevention"
              ? "งบป้องกัน"
              : formData.BudgetType === "Distribution"
              ? "งบจำหน่าย"
              : "-"}
          </Text>
        </Box>
        <Box>
          <Text fontWeight="bold">ประเภทการเบิก:</Text>
          <Text>{formData.RequestType || "-"}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">ประเภทเอกสารอ้างอิง:</Text>
          <Text>{formData.ReferenceDocumentType || "-"}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">รอบการเบิก:</Text>
          <Text>{formData.ReferenceDocumentNo || "-"}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">เจ้าหน้าที่คลัง:</Text>
          <Text>{formData.WarehouseStaffID || "-"}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">สถานะ:</Text>
          <Text>{getStatusText(formData.Status) || "-"}</Text>
        </Box>
        <Box gridColumn="1 / -1">
          <Text fontWeight="bold">รายละเอียดเพิ่มเติม:</Text>
          <Text>{formData.RequestDetail || "-"}</Text>
        </Box>
      </Grid>
      <Heading as="h2" size="md" mb={4}>
        รายการวัคซีนที่เบิก
      </Heading>
      <Table variant="simple" borderWidth={1} mb={8}>
        <Thead>
          <Tr>
            <Th borderWidth={1}>รหัสวัคซีน</Th>
            <Th borderWidth={1}>ชื่อวัคซีน</Th>
            <Th borderWidth={1}>Lot No.</Th>
            <Th borderWidth={1}>จำนวน (ขวด)</Th>
            <Th borderWidth={1}>จำนวนโดส</Th>
            <Th borderWidth={1}>วันที่ขอเบิก</Th>
          </Tr>
        </Thead>
        <Tbody>
          {formData.VaccineDetails.length > 0 ? (
            formData.VaccineDetails.map((detail, index) => (
              <Tr key={index}>
                <Td borderWidth={1}>{detail.VaccineID || "-"}</Td>
                <Td borderWidth={1}>{detail.name || "-"}</Td>
                <Td borderWidth={1}>{detail.LotID || "-"}</Td>
                <Td borderWidth={1}>{detail.RequestQuantity.toLocaleString()}</Td>
                <Td borderWidth={1}>{detail.DoseCount.toLocaleString()}</Td>
                <Td borderWidth={1}>
                  {detail.RequestDate
                    ? detail.RequestDate.toLocaleDateString("th-TH", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : "-"}
                </Td>
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan={6} textAlign="center">
                ไม่มีรายการวัคซีน
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
      <Box textAlign="right" mt={8}>
        <Text>___________________________</Text>
        <Text>ผู้จัดทำ</Text>
      </Box>
    </Box>
  );
};

// Updated LotNoCell Component
export const LotNoCell = React.memo(({ row, inventory, handleLotChange, handleAddLot, formData }) => {
  // Memoize lotInventory to prevent unnecessary re-renders
  const lotInventory = React.useMemo(
    () => inventory.filter(item => item.VaccineID === row.VaccineID && item.TotalQuantity > 0),
    [inventory, row.VaccineID]
  );

  // Memoize sortedLots to prevent triggering useEffect unnecessarily
  const sortedLots = React.useMemo(
    () => [...lotInventory].sort((a, b) => new Date(a.ProductionDate) - new Date(b.ProductionDate)),
    [lotInventory]
  );

  // Memoize handleLotChange to prevent re-renders
  const memoizedHandleLotChange = React.useCallback(
    (newLotID) => handleLotChange(row.VaccineID, row.LotID, newLotID),
    [handleLotChange, row.VaccineID, row.LotID]
  );

  // Set default LotID only if row.LotID is empty and sortedLots is available
  React.useEffect(() => {
    if (!row.LotID && sortedLots.length > 0) {
      console.log(`Setting default LotID for VaccineID: ${row.VaccineID}, LotID: ${sortedLots[0].LotID}`);
      memoizedHandleLotChange(sortedLots[0].LotID);
    }
  }, [row.LotID, sortedLots, memoizedHandleLotChange]);

  // Calculate total inventory for the vaccine
  const totalInventory = React.useMemo(
    () => lotInventory.reduce((sum, item) => sum + item.TotalQuantity, 0),
    [lotInventory]
  );

  // Find the selected lot's inventory
  const selectedLot = lotInventory.find(item => item.LotID === row.LotID);
  const selectedLotQuantity = selectedLot ? selectedLot.TotalQuantity : 0;

  // Determine warning messages
  let warningMessage = null;
  if (row.RequestQuantity > totalInventory) {
    warningMessage = `จำนวนที่ขอเบิก (${row.RequestQuantity.toLocaleString()}) เกินจำนวนคงคลังทั้งหมด (${totalInventory.toLocaleString()})`;
  } else if (row.LotID && row.RequestQuantity > selectedLotQuantity) {
    warningMessage = `จำนวนที่ขอเบิก (${row.RequestQuantity.toLocaleString()}) เกินจำนวนคงคลังของ Lot ${row.LotID} (${selectedLotQuantity.toLocaleString()})`;
  }

  // Log for debugging
  console.log(`Rendering LotNoCell for VaccineID: ${row.VaccineID}, LotID: ${row.LotID}, Lot Count: ${sortedLots.length}, RequestQuantity: ${row.RequestQuantity}, TotalInventory: ${totalInventory}`);

  return (
    <VStack align="start" spacing={2}>
      <Select
        value={row.LotID}
        onChange={(e) => memoizedHandleLotChange(e.target.value)}
        size="sm"
        width="250px"
        ml={10}
        placeholder={sortedLots.length > 0 ? "เลือก Lot" : "ไม่มี Lot ที่มีในคลัง"}
        isDisabled={sortedLots.length === 0}
      >
        {sortedLots.map((item) => (
          <option key={item.LotID} value={item.LotID}>
            {`${item.LotID} (${item.TotalQuantity.toLocaleString()} ขวด, Exp. ${new Date(
              item.ExpiryDate
            ).toLocaleDateString("th-TH", {
              year: "numeric",
              month: "numeric",
              day: "numeric",
            })})`}
          </option>
        ))}
      </Select>
      {warningMessage && (
        <Text color="red.500" fontSize="sm" ml={10}>
          {warningMessage}
        </Text>
      )}
      {row.LotID && sortedLots.length > 1 && (() => {
        // Count how many lots are already used for this vaccine
        const usedLotIds = formData.VaccineDetails
          .filter(detail => detail.VaccineID === row.VaccineID)
          .map(detail => detail.LotID);
        
        // Check if all available lots are used
        const allLotsUsed = sortedLots.every(lot => usedLotIds.includes(lot.LotID));
        
        // Only show button if not all lots are used
        return !allLotsUsed ? (
          <Button
            leftIcon={<FontAwesomeIcon icon={faPlus} />}
            size="sm"
            colorScheme="blue"
            ml={10}
            onClick={() => handleAddLot(row, selectedLotQuantity, totalInventory)}
          >
            เพิ่ม Lot
          </Button>
        ) : null;
      })()}
    </VStack>
  );
});

// VaccineRequisitionForm Component (only showing modified parts)
const VaccineRequisitionForm = () => {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const location = useLocation();
  const { sessionId, profile } = useAuth();
  const [formData, setFormData] = useState({
    TransactionID: transactionId || "RQ-XXXX-XX-XX",
    RequestDate: new Date(),
    FromWarehouseID: "",
    ToWarehouseID: "",
    BudgetType: "",
    RequestType: "",
    ReferenceDocumentType: "",
    ReferenceDocumentNo: "",
    WarehouseStaffID: profile?.Email || "",
    RequestDetail: "",
    Status: "Pending",
    VaccineDetails: [],
    deliveryAddress: "",
    deliveryBy: "",
    deliveryVehicle: "",
    deliveryStaff: "",
    deliveryRoute: "",
    trackingNumber: "",
    deliveryDate: "",
  });
  const [parentWarehouses, setParentWarehouses] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [newDetail, setNewDetail] = useState({
    VaccineID: "",
    LotID: "",
    RequestQuantity: "",
    ApprovedQuantity: "",
    ConfirmedQuantity: "",
    RequestDate: formData.RequestDate,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isInventoryLoading, setIsInventoryLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(!!transactionId);
  const [errors, setErrors] = useState({});
  const [isEditingFile, setIsEditingFile] = useState(false);
  const [temperatureFile, setTemperatureFile] = useState(null);
  const [temperatureFileUrl, setTemperatureFileUrl] = useState("");
  const [removeTemperatureFile, setRemoveTemperatureFile] = useState(false);

  // AlertDialog state - สำหรับ confirm dialogs
  const [dialogConfig, setDialogConfig] = useState({ isOpen: false, type: null });
  const cancelRef = useRef();

  // Notification dialog state - สำหรับแจ้งเตือนทั่วไป (แทน alert)
  const [notifyDialog, setNotifyDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    status: "info",
    onCloseCallback: null,
  });

  const showNotify = (title, message, status = "info", onCloseCallback = null) => {
    setNotifyDialog({ isOpen: true, title, message, status, onCloseCallback });
  };

  const closeNotify = () => {
    const cb = notifyDialog.onCloseCallback;
    setNotifyDialog({ isOpen: false, title: "", message: "", status: "info", onCloseCallback: null });
    if (cb) cb();
  };

  const notifyRef = useRef();
  const [isDeliverySaving, setIsDeliverySaving] = useState(false);
  const normalizedTemperatureFileUrl = useMemo(() => {
    if (!temperatureFileUrl) return "";
    const normalizedPath = temperatureFileUrl.startsWith("/")
      ? temperatureFileUrl
      : `/${temperatureFileUrl}`;
    return `${BASE_URL}${normalizedPath}`;
  }, [temperatureFileUrl]);
  const [originalVaccineDetails, setOriginalVaccineDetails] = useState([]);

  const isReadOnly =
    formData.Status && formData.Status !== "Pending";
  const isApprovedStatus = APPROVED_STATUSES.includes(
    formData.Status
  );

  const packageSizeMap = useMemo(() => {
    const map = {};
    vaccineOptions.forEach((vaccine) => {
      map[vaccine.VaccineID] = vaccine.PackageSize || 1;
    });
    return map;
  }, [vaccineOptions]);

  const getLatestRequisitionSnapshot = useCallback(async () => {
    if (!formData.TransactionID) {
      console.warn("Cannot print without TransactionID");
      return null;
    }

    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineRequisitionsByID",
        data: { TransactionID: formData.TransactionID },
        sessionId: sessionId || "",
      });

      if (response.result !== "success") {
        console.warn("Latest requisition API did not return success:", response);
        return null;
      }

      const requisition = response.VaccineRequisition || {};
      const detailsFromApi = response.VaccineDetails || [];

      const statusForApproval =
        requisition.Status || formData.Status || "";
      const isApprovedSnapshot = APPROVED_STATUSES.includes(
        statusForApproval
      );

      const mappedDetails = detailsFromApi.map((detail) => {
        const vaccineOption = vaccineOptions.find(
          (v) => v.VaccineID === detail.VaccineID
        );
        const packageSize =
          Number(vaccineOption?.PackageSize) ||
          Number(detail.packageSize) ||
          1;
        const name =
          vaccineOption?.VaccineNameTH ||
          detail.name ||
          detail.VaccineNameTH ||
          "ไม่พบชื่อวัคซีน";

        const requestQuantity =
          Number(detail.RequestQuantity) ||
          0;

        const approvedQuantityRaw = detail.ApprovedQuantity;
        const approvedQuantityNum =
          approvedQuantityRaw === null || approvedQuantityRaw === undefined
            ? null
            : Number(approvedQuantityRaw);
        const quantityForPrint =
          isApprovedSnapshot &&
          approvedQuantityNum !== null &&
          !Number.isNaN(approvedQuantityNum)
            ? approvedQuantityNum
            : requestQuantity;

        const requestDoseCountRaw = detail.RequestDoses;
        const requestedDoseCount =
          requestDoseCountRaw === null || requestDoseCountRaw === undefined
            ? Math.ceil(requestQuantity * packageSize)
            : Number(requestDoseCountRaw) || 0;

        const doseCountRaw = detail.DoseCount;
        const approvedDoseCount =
          doseCountRaw === null || doseCountRaw === undefined
            ? Math.ceil(quantityForPrint * packageSize)
            : Number(doseCountRaw) || 0;

        return {
          VaccineID: detail.VaccineID,
          name,
          LotID: detail.LotID || "",
          RequestQuantity: requestQuantity,
          ApprovedQuantity:
            approvedQuantityNum === null || Number.isNaN(approvedQuantityNum)
              ? null
              : approvedQuantityNum,
          ConfirmedQuantity:
            Number(detail.ConfirmedQuantity) || 0,
          QuantityForPrint: quantityForPrint,
          RequestedDoseCount: requestedDoseCount,
          DoseCount: approvedDoseCount,
          packageSize,
        };
      });

      const requestDate = requisition.RequestDate
        ? new Date(requisition.RequestDate)
        : null;

      return {
        requisition: {
          ...requisition,
          RequestDate: requestDate,
        },
        details: mappedDetails,
      };
    } catch (error) {
      console.error(
        "Error fetching latest requisition data for printing:",
        error
      );
      return null;
    }
  }, [formData.TransactionID, sessionId, vaccineOptions]);

  const originalRequestedMap = useMemo(() => {
    const map = {};
    originalVaccineDetails.forEach((detail) => {
      const current = map[detail.VaccineID] || 0;
      map[detail.VaccineID] = current + (detail.RequestQuantity || 0);
    });
    return map;
  }, [originalVaccineDetails]);

  const requestedTotals = useMemo(() => {
    const bottles = originalVaccineDetails.reduce(
      (sum, detail) => sum + (detail.RequestQuantity || 0),
      0
    );
    const doses = originalVaccineDetails.reduce((sum, detail) => {
      const packageSize =
        packageSizeMap[detail.VaccineID] || 1;
      return (
        sum +
        (detail.RequestQuantity || 0) * packageSize
      );
    }, 0);
    return { bottles, doses };
  }, [originalVaccineDetails, packageSizeMap]);

  const approvedTotals = useMemo(() => {
    return formData.VaccineDetails.reduce(
      (acc, detail) => {
        const approvedQuantity = isReadOnly
          ? detail.ApprovedQuantity || 0
          : detail.RequestQuantity || 0;
        const packageSize =
          packageSizeMap[detail.VaccineID] || 1;

        return {
          bottles: acc.bottles + approvedQuantity,
          doses:
            acc.doses +
            approvedQuantity * packageSize,
        };
      },
      { bottles: 0, doses: 0 }
    );
  }, [formData.VaccineDetails, packageSizeMap, isReadOnly]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "application/pdf": [],
      "application/xlsx": [],
      "application/xls": [],
    },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setTemperatureFile(file);
        setIsEditingFile(false);
        setRemoveTemperatureFile(false);
      }
    },
  });

  const handleEditExistingFile = () => {
    setIsEditingFile(true);
    setTemperatureFile(null);
    setRemoveTemperatureFile(false);
  };

  const handleRemoveExistingFile = () => {
    setTemperatureFile(null);
    setTemperatureFileUrl("");
    setRemoveTemperatureFile(true);
    setIsEditingFile(false);
  };

  const handleRemoveNewFile = () => {
    setTemperatureFile(null);
    if (temperatureFileUrl) {
      setIsEditingFile(false);
      setRemoveTemperatureFile(false);
    }
  };

  const handleCancelEditFile = () => {
    setTemperatureFile(null);
    setIsEditingFile(false);
    setRemoveTemperatureFile(false);
  };

  const printTable = async () => {
    const printWindow = window.open("", "", "width=800,height=600");
    if (!printWindow) {
      showNotify("ไม่สามารถดำเนินการได้", "ไม่สามารถเปิดหน้าต่างพิมพ์ได้", "warning");
      return;
    }

    const snapshot = await getLatestRequisitionSnapshot();
    if (!snapshot) {
      printWindow.document.write(
        "<p>ไม่สามารถดึงข้อมูลล่าสุดสำหรับการพิมพ์ได้</p>"
      );
      printWindow.document.close();
      return;
    }

    const { requisition, details } = snapshot;

    const getWarehouseName = (warehouseId, isParent) => {
      if (isParent) {
        const warehouse = parentWarehouses.find((w) => w.WarehouseID === warehouseId);
        return warehouse ? warehouse.WarehouseName : "-";
      }
      const warehouse = warehouses.find((w) => w.WarehouseID === warehouseId);
      return warehouse ? warehouse.WarehouseName : "-";
    };

    const budgetTypeText =
      requisition.BudgetType === "Prevention"
        ? "งบป้องกัน"
        : requisition.BudgetType === "Distribution"
        ? "งบจำหน่าย"
        : "-";

    const formattedRequestDate = requisition.RequestDate
      ? requisition.RequestDate.toLocaleDateString("th-TH", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "-";

    const printContent = `
      <html>
        <head>
          <title>Vaccine Requisition - ${requisition.TransactionID}</title>
          <style>
            body { font-family: Sarabun, Arial, sans-serif; margin: 20px; }
            h1, h2 { text-align: center; }
            h3 { text-align: center; margin: 10px 0; }            
            .header-info { margin-bottom: 5px; }
            .header-row { display: flex; justify-content: margin-bottom: 5px; }
            .header-item { flex: 1; min-width: 250px; }
            .header-top { display:flex; justify-content: space-between; align-items:flex-start; }

            .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin-bottom: 5px; }
            .grid-item { margin-bottom: 5px; }
            .grid-item span { font-weight: bold; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 30px; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .totals-row { font-weight: bold; background-color: #f0f0f0; }
            .totals-row td { text-align: right; }
            .totals-row td:nth-child(2) { text-align: left; }
            .notes { margin-top: 20px; }
            .notes p { margin: 5px 0; }
            .signature { text-align: right; margin-top: 32px; }
          </style>
        </head>
        <body>
          <h2>ใบเบิกวัคซีน</h2>
          <div class="grid">
            <div class="grid-item">
              <span>เลขที่เอกสาร:</span> ${requisition.TransactionID || "-"}
            </div>
            <div class="grid-item">
              <span>วันที่ขอเบิก:</span> ${formattedRequestDate}
            </div>
            <div class="grid-item">
              <span>คลังต้นทาง:</span> ${getWarehouseName(requisition.FromWarehouseID, true) || "-"}
            </div>
            <div class="grid-item">
              <span>คลังปลายทาง:</span> ${getWarehouseName(requisition.ToWarehouseID, false) || "-"}
            </div>
            <div class="grid-item">
              <span>ประเภทงบประมาณ:</span> ${budgetTypeText}
            </div>
            <div class="grid-item">
              <span>ประเภทการเบิก:</span> ${requisition.RequestType || "-"}
            </div>
            <div class="grid-item">
              <span>ประเภทเอกสารอ้างอิง:</span> ${requisition.ReferenceDocumentType || "-"}
            </div>
            <div class="grid-item">
              <span>รอบการเบิก:</span> ${requisition.ReferenceDocumentNo || "-"}
            </div>
            <div class="grid-item">
              <span>เจ้าหน้าที่คลัง:</span> ${requisition.WarehouseStaffID || "-"}
            </div>
            <div class="grid-item">
              <span>สถานะ:</span> ${getStatusText(requisition.Status) || "-"}
            </div>
            <div class="grid-item" style="grid-column: 1 / -1;">
              <span>รายละเอียดเพิ่มเติม:</span> ${requisition.RequestDetail || "-"}
            </div>
          </div>
          <h2>รายการวัคซีนที่เบิก</h2>
          <table>
            <thead>
              <tr>
                <th>รหัสวัคซีน</th>
                <th>ชื่อวัคซีน</th>
                <th>Lot No.</th>
                <th style="text-align:right;">จำนวน (ขวด)</th>
                <th style="text-align:right;">จำนวนโดส</th>
                <th>หมายเหตุ</th>
              </tr>
            </thead>
            <tbody>
              ${
                details.length > 0
                  ? details
                      .map(
                      (detail) => `
                        <tr>
                          <td>${detail.VaccineID || "-"}</td>
                          <td>${detail.name || "-"}</td>
                          <td>${detail.LotID || "-"}</td>
                          <td style="text-align:right;">${detail.QuantityForPrint.toLocaleString()}</td>
                          <td style="text-align:right;">${detail.DoseCount.toLocaleString()}</td>
                          <td></td>
                        </tr>
                      `
                    )
                      .join("")
                  : `
                        <tr>
                          <td colspan="6" style="text-align: center;">ไม่มีรายการวัคซีน</td>
                        </tr>
                    `
              }
            </tbody>
          </table>
          <div class="signature">
            <table border="0" cellpadding="5" cellspacing="0">
              <tbody>
                <tr>
                  <td style="border:0; text-align: right;" width="85%">ผู้จัดทำ...........................................</td>
                </tr>
                <tr>
                  <td style="border:0; text-align: right;">ตำแหน่ง...........................................</td>
                </tr>
                <tr>
                  <td style="border:0; text-align: right;">วันที่.........../................/............</td>
                </tr>
              </tbody>            
            </table>
          </div>
        </body>
      </html>
    `;
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const printDeliveryNote = async () => {
    if (!APPROVED_STATUSES.includes(formData.Status)) {
      showNotify("ไม่สามารถพิมพ์ได้", "ใบนำส่งวัคซีนสามารถพิมพ์ได้หลังอนุมัติจ่ายแล้วเท่านั้น", "warning");
      return;
    }

    const printWindow = window.open("", "", "width=800,height=600");
    if (!printWindow) {
      showNotify("ไม่สามารถดำเนินการได้", "ไม่สามารถเปิดหน้าต่างพิมพ์ได้", "warning");
      return;
    }

    const snapshot = await getLatestRequisitionSnapshot();
    if (!snapshot) {
      printWindow.document.write(
        "<p>ไม่สามารถดึงข้อมูลล่าสุดสำหรับการพิมพ์ได้</p>"
      );
      printWindow.document.close();
      return;
    }

    const { requisition, details } = snapshot;

    if (!APPROVED_STATUSES.includes(requisition.Status || "")) {
      printWindow.document.write(
        "<p>ใบนำส่งวัคซีนสามารถพิมพ์ได้หลังอนุมัติจ่ายแล้วเท่านั้น</p>"
      );
      printWindow.document.close();
      return;
    }

    const getWarehouseName = (warehouseId, isParent) => {
      if (isParent) {
        const warehouse = parentWarehouses.find((w) => w.WarehouseID === warehouseId);
        return warehouse ? warehouse.WarehouseName : "-";
      }
      const warehouse = warehouses.find((w) => w.WarehouseID === warehouseId);
      return warehouse ? warehouse.WarehouseName : "-";
    };

    const budgetTypeText =
      requisition.BudgetType === "Prevention"
        ? "งบป้องกัน"
        : requisition.BudgetType === "Distribution"
        ? "งบจำหน่าย"
        : "-";

    const formattedRequestDate = requisition.RequestDate
      ? requisition.RequestDate.toLocaleDateString("th-TH", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "-";

    const printContent = `
      <html>
        <head>
          <title>Vaccine Delivery Note - ${requisition.TransactionID}</title>
          <style>
            body { font-family: Sarabun, Arial, sans-serif; margin: 20px; }
            h1, h2 { text-align: center; }
            h3 { text-align: center; margin: 10px 0; }            
            .header-info { margin-bottom: 5px; }
            .header-row { display: flex; justify-content: margin-bottom: 5px; }
            .header-item { flex: 1; min-width: 250px; }
            .header-top { display:flex; justify-content: space-between; align-items:flex-start; }

            .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin-bottom: 5px; }
            .grid-item { margin-bottom: 5px; }
            .grid-item span { font-weight: bold; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 30px; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .totals-row { font-weight: bold; background-color: #f0f0f0; }
            .totals-row td { text-align: right; }
            .totals-row td:nth-child(2) { text-align: left; }
            .notes { margin-top: 20px; }
            .notes p { margin: 5px 0; }
            .signature { text-align: right; margin-top: 32px; }
          </style>
        </head>
        <body>
          <h2>ใบนำส่งวัคซีน</h2>
          <div class="grid">
            <div class="grid-item">
              <span>เลขที่เอกสาร:</span> ${requisition.TransactionID || "-"}
            </div>
            <div class="grid-item">
              <span>วันที่ขอเบิก:</span> ${formattedRequestDate}
            </div>
            <div class="grid-item">
              <span>คลังต้นทาง:</span> ${getWarehouseName(requisition.FromWarehouseID, true) || "-"}
            </div>
            <div class="grid-item">
              <span>คลังปลายทาง:</span> ${getWarehouseName(requisition.ToWarehouseID, false) || "-"}
            </div>
            <div class="grid-item">
              <span>ประเภทงบประมาณ:</span> ${budgetTypeText}
            </div>
            <div class="grid-item">
              <span>ประเภทการเบิก:</span> ${requisition.RequestType || "-"}
            </div>
            <div class="grid-item">
              <span>ประเภทเอกสารอ้างอิง:</span> ${requisition.ReferenceDocumentType || "-"}
            </div>
            <div class="grid-item">
              <span>รอบการเบิก:</span> ${requisition.ReferenceDocumentNo || "-"}
            </div>
            <div class="grid-item">
              <span>เจ้าหน้าที่คลัง:</span> ${requisition.WarehouseStaffID || "-"}
            </div>
            <div class="grid-item">
              <span>สถานะ:</span> ${getStatusText(requisition.Status) || "-"}
            </div>
            <div class="grid-item" style="grid-column: 1 / -1;">
              <span>รายละเอียดเพิ่มเติม:</span> ${requisition.RequestDetail || "-"}
            </div>
          </div>
          <h2>รายการวัคซีนที่นำส่ง</h2>
          <table>
            <thead>
              <tr>
                <th>รหัสวัคซีน</th>
                <th>ชื่อวัคซีน</th>
                <th>Lot No.</th>
                <th style="text-align:right;">จำนวน (ขวด)</th>
                <th style="text-align:right;">จำนวนโดส</th>
                <th>หมายเหตุ</th>
              </tr>
            </thead>
            <tbody>
              ${
                details.length > 0
                  ? details
                      .map(
                      (detail) => `
                        <tr>
                          <td>${detail.VaccineID || "-"}</td>
                          <td>${detail.name || "-"}</td>
                          <td>${detail.LotID || "-"}</td>
                          <td style="text-align:right;">${detail.QuantityForPrint.toLocaleString()}</td>
                          <td style="text-align:right;">${detail.DoseCount.toLocaleString()}</td>
                          <td></td>
                        </tr>
                      `
                    )
                      .join("")
                  : `
                        <tr>
                          <td colspan="6" style="text-align: center;">ไม่มีรายการวัคซีน</td>
                        </tr>
                    `
              }
            </tbody>
          </table>
          <div class="signature">
            <table border="0" cellpadding="5" cellspacing="0">
              <tbody>
                <tr>
                  <td style="border:0; text-align: right;" width="85%">ผู้จัดทำ / ผู้นำส่ง...........................................</td>
                </tr>
                <tr>
                  <td style="border:0; text-align: right;">ตำแหน่ง...........................................</td>
                </tr>
                <tr>
                  <td style="border:0; text-align: right;">วันที่.........../................/............</td>
                </tr>
              </tbody>            
            </table>
          </div>
        </body>
      </html>
    `;
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const printInspectionNote = async () => {
    if (!APPROVED_STATUSES.includes(formData.Status)) {
      showNotify("ไม่สามารถพิมพ์ได้", "ใบตรวจนับวัคซีนสามารถพิมพ์ได้หลังอนุมัติจ่ายแล้วเท่านั้น", "warning");
      return;
    }

    const printWindow = window.open("", "", "width=800,height=600");
    if (!printWindow) {
      showNotify("ไม่สามารถดำเนินการได้", "ไม่สามารถเปิดหน้าต่างพิมพ์ได้", "warning");
      return;
    }

    const snapshot = await getLatestRequisitionSnapshot();
    if (!snapshot) {
      printWindow.document.write(
        "<p>ไม่สามารถดึงข้อมูลล่าสุดสำหรับการพิมพ์ได้</p>"
      );
      printWindow.document.close();
      return;
    }

    const { requisition, details } = snapshot;

    if (!APPROVED_STATUSES.includes(requisition.Status || "")) {
      printWindow.document.write(
        "<p>ใบตรวจนับวัคซีนสามารถพิมพ์ได้หลังอนุมัติจ่ายแล้วเท่านั้น</p>"
      );
      printWindow.document.close();
      return;
    }

    const getWarehouseName = (warehouseId, isParent) => {
      if (isParent) {
        const warehouse = parentWarehouses.find((w) => w.WarehouseID === warehouseId);
        return warehouse ? warehouse.WarehouseName : "-";
      }
      const warehouse = warehouses.find((w) => w.WarehouseID === warehouseId);
      return warehouse ? warehouse.WarehouseName : "-";
    };

    const budgetTypeText =
      requisition.BudgetType === "Prevention"
        ? "งบป้องกัน"
        : requisition.BudgetType === "Distribution"
        ? "งบจำหน่าย"
        : "-";

    const formattedRequestDate = requisition.RequestDate
      ? requisition.RequestDate.toLocaleDateString("th-TH", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "-";

    const printContent = `
      <html>
        <head>
          <title>Vaccine Inspection Note - ${requisition.TransactionID}</title>
          <style>
            body { font-family: Sarabun, Arial, sans-serif; margin: 20px; }
            h1, h2 { text-align: center; }
            h3 { text-align: center; margin: 10px 0; }            
            .header-info { margin-bottom: 5px; }
            .header-row { display: flex; justify-content: margin-bottom: 5px; }
            .header-item { flex: 1; min-width: 250px; }
            .header-top { display:flex; justify-content: space-between; align-items:flex-start; }

            .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin-bottom: 5px; }
            .grid-item { margin-bottom: 5px; }
            .grid-item span { font-weight: bold; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 30px; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .totals-row { font-weight: bold; background-color: #f0f0f0; }
            .totals-row td { text-align: right; }
            .totals-row td:nth-child(2) { text-align: left; }
            .notes { margin-top: 20px; }
            .notes p { margin: 5px 0; }
            .signature { text-align: right; margin-top: 32px; }
          </style>
        </head>
        <body>
          <h2>ใบตรวจนับวัคซีน</h2>
          <div class="grid">
            <div class="grid-item">
              <span>เลขที่เอกสาร:</span> ${requisition.TransactionID || "-"}
            </div>
            <div class="grid-item">
              <span>วันที่ขอเบิก:</span> ${formattedRequestDate}
            </div>
            <div class="grid-item">
              <span>คลังต้นทาง:</span> ${getWarehouseName(requisition.FromWarehouseID, true) || "-"}
            </div>
            <div class="grid-item">
              <span>คลังปลายทาง:</span> ${getWarehouseName(requisition.ToWarehouseID, false) || "-"}
            </div>
            <div class="grid-item">
              <span>ประเภทงบประมาณ:</span> ${budgetTypeText}
            </div>
            <div class="grid-item">
              <span>ประเภทการเบิก:</span> ${requisition.RequestType || "-"}
            </div>
            <div class="grid-item">
              <span>ประเภทเอกสารอ้างอิง:</span> ${requisition.ReferenceDocumentType || "-"}
            </div>
            <div class="grid-item">
              <span>รอบการเบิก:</span> ${requisition.ReferenceDocumentNo || "-"}
            </div>
            <div class="grid-item">
              <span>เจ้าหน้าที่คลัง:</span> ${requisition.WarehouseStaffID || "-"}
            </div>
            <div class="grid-item">
              <span>สถานะ:</span> ${getStatusText(requisition.Status) || "-"}
            </div>
            <div class="grid-item" style="grid-column: 1 / -1;">
              <span>รายละเอียดเพิ่มเติม:</span> ${requisition.RequestDetail || "-"}
            </div>
          </div>
          <h2>รายการวัคซีนที่ตรวจนับ</h2>
          <table>
            <thead>
              <tr>
                <th>รหัสวัคซีน</th>
                <th>ชื่อวัคซีน</th>
                <th>Lot No.</th>
                <th style="text-align:right;">จำนวน (ขวด)</th>
                <th style="text-align:right;">จำนวนโดส</th>
                <th>หมายเหตุ</th>
                <th>การตรวจสอบ</th>
              </tr>
            </thead>
            <tbody>
              ${
                details.length > 0
                  ? details
                      .map(
                      (detail) => `
                        <tr>
                          <td>${detail.VaccineID || "-"}</td>
                          <td>${detail.name || "-"}</td>
                          <td>${detail.LotID || "-"}</td>
                          <td style="text-align:right;">${detail.QuantityForPrint.toLocaleString()}</td>
                          <td style="text-align:right;">${detail.DoseCount.toLocaleString()}</td>
                          <td></td>
                          <td></td>
                        </tr>
                      `
                    )
                      .join("")
                  : `
                        <tr>
                          <td colspan="7" style="text-align: center;">ไม่มีรายการวัคซีน</td>
                        </tr>
                    `
              }
            </tbody>
          </table>
          <div class="signature">
            <table border="0" cellpadding="5" cellspacing="0">
              <tbody>
                <tr>
                  <td style="border:0; text-align: right;" width="85%">ผู้จัดทำ / ผู้ตรวจนับ...........................................</td>
                </tr>
                <tr>
                  <td style="border:0; text-align: right;">ตำแหน่ง...........................................</td>
                </tr>
                <tr>
                  <td style="border:0; text-align: right;">วันที่.........../................/............</td>
                </tr>
              </tbody>            
            </table>
          </div>
        </body>
      </html>
    `;
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const printPackingLabel = async (row) => {
    if (!isApprovedStatus) {
      showNotify("ไม่สามารถพิมพ์ได้", "สามารถพิมพ์ Packing Label ได้หลังจากอนุมัติจ่ายแล้วเท่านั้น", "warning");
      return;
    }

    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineInventoryByLot",
        data: {
          WarehouseID: formData.FromWarehouseID,
          BudgetType: formData.BudgetType,
          VaccineID: row.VaccineID,
          LotID: row.LotID,
        },
        sessionId: sessionId || "",
      });

      if (response.result === "success" && response.InventoryReport?.length > 0) {
        const inventoryData = response.InventoryReport[0];
        const productionDate = new Date(inventoryData.ProductionDate).toLocaleDateString("th-TH", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
        const expiryDate = new Date(inventoryData.ExpiryDate).toLocaleDateString("th-TH", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });

        const quantityForLabel = Number(
          row.ApprovedQuantity ?? row.RequestQuantity ?? 0
        );

        const printContent = `
          <html>
            <head>
              <title>Packing Label - ${row.VaccineID} - ${row.LotID}</title>
              <style>
                body { 
                  font-family: Arial, sans-serif; 
                  margin: 20px; 
                  text-align: center; 
                }
                h1 { 
                  font-size: 20px; 
                  margin-bottom: 10px; 
                }
                .label { 
                  border: 1px solid black; 
                  padding: 15px; 
                  display: inline-block; 
                  text-align: left; 
                  width: 300px; 
                }
                .label div { 
                  margin-bottom: 8px; 
                }
                .label div span { 
                  font-weight: bold; 
                }
              </style>
            </head>
            <body>
              <h1>Packing Label</h1>
              <div class="label">
                <div><span>ชื่อวัคซีน:</span> ${row.name || "-"}</div>
                <div><span>Lot:</span> ${row.LotID || "-"}</div>
                <div><span>จำนวน:</span> ${quantityForLabel.toLocaleString()} ขวด</div>
                <div><span>วันที่ผลิต:</span> ${productionDate}</div>
                <div><span>วันหมดอายุ:</span> ${expiryDate}</div>
              </div>
            </body>
          </html>
        `;
        const newWindow = window.open("", "", "width=400,height=300");
        newWindow.document.write(printContent);
        newWindow.document.close();
        newWindow.print();
      } else {
        showNotify("ไม่พบข้อมูล", "ไม่พบข้อมูลสำหรับ Lot นี้ในคลัง", "warning");
      }
    } catch (error) {
      console.error("Error fetching inventory for packing label:", error);
      showNotify("เกิดข้อผิดพลาด", "เกิดข้อผิดพลาดในการดึงข้อมูลสำหรับ Packing Label: " + error.message, "error");
    }
  };

  // Sync newDetail.RequestDate with formData.RequestDate
  useEffect(() => {
    setNewDetail((prev) => ({ ...prev, RequestDate: formData.RequestDate }));
  }, [formData.RequestDate]);

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const fetchParentWarehouses = async () => {
    try {
      console.log("Fetching parent warehouses with sessionId:", sessionId);
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      console.log("Parent Warehouses API Response:", response);
      if (response.result === "success") {
        const warehouses = response.VaccineWarehousesList || response.VaccineParentWarehousesList || response.ParentWarehousesList || [];
        console.log("Setting parentWarehouses:", warehouses);
        setParentWarehouses(warehouses);
        return warehouses;
      } else {
        console.warn("Parent Warehouses API returned non-success result:", response);
        setParentWarehouses([]);
        return [];
      }
    } catch (error) {
      console.error("Error fetching parent warehouses:", error);
      setParentWarehouses([]);
      return [];
    }
  };

  const fetchWarehouses = async () => {
    try {
      console.log("Fetching warehouses with sessionId:", sessionId);
      const response = await apiService({
        method: "POST",
        url: "/getVaccineChildWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      console.log("Warehouses API Response:", response);
      if (response.result === "success") {
        const warehouses = response.VaccineWarehousesList || [];
        console.log("Setting warehouses:", warehouses);
        setWarehouses(warehouses);
        return warehouses;
      } else {
        console.warn("Warehouses API returned non-success result:", response);
        setWarehouses([]);
        return [];
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
      setWarehouses([]);
      return [];
    }
  };

  const fetchVaccineOptions = async () => {
    try {
      console.log("Fetching vaccine options with sessionId:", sessionId);
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      console.log("Vaccine Options API Response:", response);
      if (response.result === "success") {
        const vaccines = response.VaccinesList || [];
        setVaccineOptions(vaccines);
        return vaccines;
      }
      return [];
    } catch (error) {
      console.error("Error fetching vaccines:", error);
      return [];
    }
  };

  const fetchInventory = async (warehouseId, budgetType, vaccineId) => {
    if (!warehouseId || !budgetType || !vaccineId) {
      return [];
    }
    // Skip if inventory for this VaccineID already exists
    if (inventory.some(item => item.VaccineID === vaccineId)) {
      console.log(`Skipping fetchInventory for VaccineID: ${vaccineId}, already in inventory`);
      return inventory.filter(item => item.VaccineID === vaccineId);
    }
    try {
      console.log(`Fetching inventory for WarehouseID: ${warehouseId}, BudgetType: ${budgetType}, VaccineID: ${vaccineId}`);
      const response = await apiService({
        method: "POST",
        url: "/getVaccineInventoryByLot",
        data: {
          WarehouseID: warehouseId,
          BudgetType: budgetType,
          VaccineID: vaccineId,
        },
        sessionId: sessionId || "",
      });
      console.log(`Inventory API Response for VaccineID: ${vaccineId}`, response);
      if (response.result === "success") {
        return response.InventoryReport || [];
      }
      return [];
    } catch (error) {
      console.error(`Error fetching inventory for VaccineID: ${vaccineId}:`, error);
      return [];
    }
  };

  const fetchAllInventories = async (warehouseId, budgetType, vaccineIds) => {
    if (!warehouseId || !budgetType || !vaccineIds.length) {
      setInventory([]);
      return;
    }
    setIsInventoryLoading(true);
    try {
      console.log(`Fetching inventories for VaccineIDs: ${vaccineIds.join(", ")}`);
      const inventoryPromises = vaccineIds.map(vaccineId =>
        fetchInventory(warehouseId, budgetType, vaccineId)
      );
      const inventories = await Promise.all(inventoryPromises);
      // Flatten and merge inventories, ensuring no duplicates
      const mergedInventory = inventories.flat().reduce((acc, item) => {
        const existing = acc.find(i => i.VaccineID === item.VaccineID && i.LotID === item.LotID);
        if (!existing) {
          acc.push(item);
        }
        return acc;
      }, []);
      console.log("Merged inventory:", mergedInventory);
      setInventory(mergedInventory);
    } catch (error) {
      console.error("Error fetching all inventories:", error);
      setInventory([]);
    } finally {
      setIsInventoryLoading(false);
    }
  };

  const fetchRequisition = async (id, vaccines) => {
    setIsLoading(true);
    try {
      console.log("Fetching requisition for TransactionID:", id);
      const response = await apiService({
        method: "POST",
        url: "/getVaccineRequisitionsByID",
        data: { TransactionID: id },
        sessionId: sessionId || "",
      });
      console.log("Requisition API Response:", response);
      if (response.result === "success") {
        const requisition = response.VaccineRequisition;
        const details = response.VaccineDetails || [];
        const updatedDetails = details.map((detail) => {
          const vaccine = vaccines.find((v) => v.VaccineID === detail.VaccineID);
          const packageSize = vaccine?.PackageSize || 1;
          const doseCount = Math.ceil(detail.RequestQuantity * packageSize);
          return {
            VaccineID: detail.VaccineID,
            name: vaccine ? vaccine.VaccineNameTH : "ไม่พบชื่อวัคซีน",
            LotID: detail.LotID,
            RequestQuantity: detail.RequestQuantity,
            ApprovedQuantity: detail.ApprovedQuantity,
            ConfirmedQuantity: detail.ConfirmedQuantity,
            RequestDate: new Date(detail.RequestDate),
            DoseCount: doseCount,
          };
        });
        setFormData({
          TransactionID: requisition.TransactionID,
          RequestDate: new Date(requisition.RequestDate),
          FromWarehouseID: requisition.FromWarehouseID,
          ToWarehouseID: requisition.ToWarehouseID,
          BudgetType: requisition.BudgetType,
          RequestType: requisition.RequestType,
          ReferenceDocumentType: requisition.ReferenceDocumentType,
          ReferenceDocumentNo: requisition.ReferenceDocumentNo,
          WarehouseStaffID: requisition.WarehouseStaffID,
          RequestDetail: requisition.RequestDetail,
          Status: requisition.Status,
          VaccineDetails: updatedDetails,
          deliveryAddress: requisition.DeliveryAddress || requisition.deliveryAddress || "",
          deliveryBy: requisition.DeliveryBy || requisition.deliveryBy || "",
          deliveryVehicle: requisition.DeliveryVehicle || requisition.deliveryVehicle || "",
          deliveryStaff: requisition.DeliveryStaff || requisition.deliveryStaff || "",
          deliveryRoute: requisition.DeliveryRoute || requisition.deliveryRoute || "",
          trackingNumber: requisition.TrackingNumber || requisition.trackingNumber || "",
          deliveryDate: requisition.DeliveryDate
            ? requisition.DeliveryDate.split("T")[0]
            : "",
        });
        const temperaturePath =
          requisition.TemperatureFilePath ||
          requisition.TemperatureFileUrl ||
          "";
        setTemperatureFile(null);
        setTemperatureFileUrl(temperaturePath);
        setRemoveTemperatureFile(false);
        setIsEditingFile(false);
        
        // เก็บจำนวนต้นฉบับจาก API
        setOriginalVaccineDetails(updatedDetails);
        // Fetch inventory for all VaccineIDs in details
        if (details.length > 0) {
          const vaccineIds = [...new Set(details.map(detail => detail.VaccineID))];
          fetchAllInventories(requisition.FromWarehouseID, requisition.BudgetType, vaccineIds);
        }
      }
    } catch (error) {
      console.error("Error fetching requisition:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (sessionId) {
        console.log("Starting loadData with sessionId:", sessionId);
        const [parentWarehousesResult, warehousesResult] = await Promise.all([
          fetchParentWarehouses(),
          fetchWarehouses(),
        ]);
        console.log("loadData - Parent Warehouses:", parentWarehousesResult);
        console.log("loadData - Warehouses:", warehousesResult);
        const vaccines = await fetchVaccineOptions();
        if (transactionId) {
          await fetchRequisition(transactionId, vaccines);
        }
      } else {
        console.warn("No sessionId, skipping loadData");
      }
    };
    loadData();
  }, [sessionId, transactionId]);

  useEffect(() => {
    console.log("parentWarehouses state updated:", parentWarehouses);
  }, [parentWarehouses]);

  useEffect(() => {
    console.log("warehouses state updated:", warehouses);
  }, [warehouses]);

  // Fetch inventory for all VaccineDetails when FromWarehouseID or BudgetType changes
  useEffect(() => {
    if (formData.FromWarehouseID && formData.BudgetType && formData.VaccineDetails.length > 0) {
      const vaccineIds = [...new Set(formData.VaccineDetails.map(detail => detail.VaccineID))];
      if (newDetail.VaccineID && !vaccineIds.includes(newDetail.VaccineID)) {
        vaccineIds.push(newDetail.VaccineID);
      }
      fetchAllInventories(formData.FromWarehouseID, formData.BudgetType, vaccineIds);
    } else {
      setInventory([]);
    }
  }, [formData.FromWarehouseID, formData.BudgetType, formData.VaccineDetails, newDetail.VaccineID]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Input changed: ${name} = ${value}`);
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleDateChange = (date) => {
    console.log("Date changed:", date);
    setFormData({ ...formData, RequestDate: date });
  };

  const handleDetailInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Detail input changed: ${name} = ${value}`);
    setNewDetail({ ...newDetail, [name]: value });
  };

  const handleDetailDateChange = (date) => {
    console.log("Detail date changed:", date);
    setNewDetail({ ...newDetail, RequestDate: date });
  };

  const handleQuantityChange = (vaccineId, lotId, value) => {
    const newQuantity = value === "" ? 0 : parseInt(value, 10);
    console.log(`Quantity changed for VaccineID: ${vaccineId}, LotID: ${lotId}, New Quantity: ${newQuantity}`);
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId && detail.LotID === lotId) {
        const packageSize =
          vaccineOptions.find((v) => v.VaccineID === vaccineId)?.PackageSize || 1;
        const newDoseCount = Math.ceil(newQuantity * packageSize);
        return { ...detail, RequestQuantity: newQuantity, DoseCount: newDoseCount };
      }
      return detail;
    });
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  const handleLotChange = React.useCallback((vaccineId, oldLotId, newLotId) => {
    console.log(`Lot changed for VaccineID: ${vaccineId}, Old LotID: ${oldLotId}, New LotID: ${newLotId}`);
    setFormData(prev => ({
      ...prev,
      VaccineDetails: prev.VaccineDetails.map(detail =>
        detail.VaccineID === vaccineId && detail.LotID === oldLotId
          ? { ...detail, LotID: newLotId }
          : detail
      ),
    }));
  }, []);

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "FromWarehouseID":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    console.log("Validation errors:", newErrors);
    setErrors(newErrors);
  };

  const isFormValid = () => {
    const valid = formData.FromWarehouseID && Object.keys(errors).length === 0;
    console.log("Form valid:", valid);
    return valid;
  };

  // Add new handler for adding a new Lot
  const handleAddLot = useCallback((row, selectedLotQuantity, totalInventory) => {
    console.log(`Adding new Lot for VaccineID: ${row.VaccineID}, Current LotID: ${row.LotID}, RequestQuantity: ${row.RequestQuantity}, SelectedLotQuantity: ${selectedLotQuantity}`);
    
    const selectedVaccine = vaccineOptions.find(v => v.VaccineID === row.VaccineID);
    if (!selectedVaccine) {
      console.warn(`VaccineID ${row.VaccineID} not found in vaccineOptions`);
      return;
    }
    
    const packageSize = selectedVaccine.PackageSize || 1;
    
    // Find all available Lots for this vaccine (excluding already used ones)
    const usedLotIds = formData.VaccineDetails
      .filter(detail => detail.VaccineID === row.VaccineID)
      .map(detail => detail.LotID);
    
    const availableLots = inventory
      .filter(item => 
        item.VaccineID === row.VaccineID && 
        item.TotalQuantity > 0 && 
        !usedLotIds.includes(item.LotID)
      )
      .sort((a, b) => new Date(a.ProductionDate) - new Date(b.ProductionDate));
    
    if (availableLots.length === 0) {
      showNotify("ไม่พบข้อมูล", "ไม่มี Lot อื่นที่สามารถใช้ได้", "warning");
      return;
    }
    
    // Find all details for this vaccine
    const vaccineDetails = formData.VaccineDetails.filter(detail => detail.VaccineID === row.VaccineID);
    
    console.log(`All vaccine details for ${row.VaccineID}:`, vaccineDetails);
    
    // Calculate total used quantity
    const usedQuantity = vaccineDetails.reduce((sum, detail) => sum + detail.RequestQuantity, 0);
    
    // Use the current row's RequestQuantity as the original request quantity
    // This is the quantity that was originally requested for this vaccine
    const originalRequestQuantity = row.RequestQuantity;
    
    // Calculate remaining quantity
    const remainingQuantity = originalRequestQuantity - usedQuantity;
    
    console.log(`Debug - VaccineID: ${row.VaccineID}`);
    console.log(`Debug - Original Request Quantity (from current row): ${originalRequestQuantity}`);
    console.log(`Debug - Used Quantity: ${usedQuantity}`);
    console.log(`Debug - Remaining Quantity: ${remainingQuantity}`);
    console.log(`Debug - Current row:`, row);
    
    // Find the next available Lot
    const nextLot = availableLots[0];
    
    // Check if current lot has enough quantity
    const currentLotQuantity = row.RequestQuantity;
    const currentLotCapacity = selectedLotQuantity; // This is the available quantity in current lot
    
    let newQuantityForCurrentLot = currentLotQuantity;
    let quantityForNewLot = 0;
    
    if (currentLotQuantity > currentLotCapacity) {
      // Case 1: Current lot doesn't have enough quantity
      // Adjust current lot to maximum capacity
      newQuantityForCurrentLot = currentLotCapacity;
      // New lot gets the difference
      quantityForNewLot = currentLotQuantity - currentLotCapacity;
      
      console.log(`Adjusting: Current lot ${row.LotID} from ${currentLotQuantity} to ${newQuantityForCurrentLot}, New lot gets ${quantityForNewLot}`);
    } else {
      // Case 2: Current lot has enough quantity
      // Keep current lot as is, new lot starts with 0 for distribution purposes
      quantityForNewLot = 0;
      
      console.log(`Distribution: Current lot ${row.LotID} keeps ${currentLotQuantity}, New lot starts with ${quantityForNewLot}`);
    }
    
    // Update the current row with adjusted quantity
    const updatedDetails = formData.VaccineDetails.map(detail => {
      if (detail.VaccineID === row.VaccineID && detail.LotID === row.LotID) {
        const newDoseCount = Math.ceil(newQuantityForCurrentLot * packageSize);
        return {
          ...detail,
          RequestQuantity: newQuantityForCurrentLot,
          DoseCount: newDoseCount,
        };
      }
      return detail;
    });
    
    // Add new row for the next Lot
    const newDetail = {
      VaccineID: row.VaccineID,
      name: selectedVaccine.VaccineNameTH,
      LotID: nextLot.LotID,
      RequestQuantity: quantityForNewLot,
      RequestDate: row.RequestDate || formData.RequestDate,
      DoseCount: Math.ceil(quantityForNewLot * packageSize),
    };
    
    // Find the index of the current row to insert new row below it
    const currentRowIndex = updatedDetails.findIndex(detail => 
      detail.VaccineID === row.VaccineID && detail.LotID === row.LotID
    );
    
    // Insert new row below the current row
    const newVaccineDetails = [...updatedDetails];
    newVaccineDetails.splice(currentRowIndex + 1, 0, newDetail);
    
    setFormData(prev => ({
      ...prev,
      VaccineDetails: newVaccineDetails,
    }));
  }, [formData, vaccineOptions, inventory]);

  // Modified handleAddDetail to ensure consistency
  const handleAddDetail = () => {
    console.log("Adding detail:", newDetail);
    if (!newDetail.VaccineID || newDetail.RequestQuantity <= 0) {
      showNotify("ข้อมูลไม่ครบ", "กรุณาเลือกวัคซีนและระบุจำนวนที่มากกว่า 0", "warning");
      return;
    }

    const selectedVaccine = vaccineOptions.find(
      (v) => v.VaccineID === newDetail.VaccineID
    );
    if (!selectedVaccine) {
      console.warn(`VaccineID ${newDetail.VaccineID} not found in vaccineOptions`);
      return;
    }

    const packageSize = selectedVaccine.PackageSize || 1;
    const requestQuantity = parseInt(newDetail.RequestQuantity);
    const doseCount = Math.ceil(requestQuantity * packageSize);

    // Calculate total inventory for the selected vaccine
    const totalInventory = inventory
      .filter(item => item.VaccineID === newDetail.VaccineID && item.TotalQuantity > 0)
      .reduce((sum, item) => sum + item.TotalQuantity, 0);

    if (requestQuantity > totalInventory) {
      showNotify("จำนวนเกินคงคลัง", `จำนวนที่ขอเบิก (${requestQuantity}) เกินจำนวนคงคลังทั้งหมด (${totalInventory})`, "warning");
      return;
    }

    if (newDetail.LotID) {
      const selectedLot = inventory.find(
        (item) => item.LotID === newDetail.LotID
      );
      if (!selectedLot) {
        showNotify("ไม่พบ Lot", "Lot ที่เลือกไม่พบในคลัง", "warning");
        return;
      }
      if (requestQuantity > selectedLot.TotalQuantity) {
        showNotify("จำนวนเกินคงคลัง", `จำนวนที่ขอเบิก (${requestQuantity}) เกินจำนวนคงคลังของ Lot ${selectedLot.LotID} (${selectedLot.TotalQuantity})`, "warning");
        return;
      }
      const detailData = {
        VaccineID: newDetail.VaccineID,
        name: selectedVaccine.VaccineNameTH,
        LotID: newDetail.LotID,
        RequestQuantity: requestQuantity,
        RequestDate: newDetail.RequestDate || formData.RequestDate,
        DoseCount: doseCount,
      };
      setFormData((prevFormData) => ({
        ...prevFormData,
        VaccineDetails: [...prevFormData.VaccineDetails, detailData],
      }));
    } else {
      let remainingQuantity = requestQuantity;
      const detailsToAdd = [];
      const sortedInventory = inventory
        .filter(item => item.VaccineID === newDetail.VaccineID && item.TotalQuantity > 0)
        .sort((a, b) => new Date(a.ProductionDate) - new Date(b.ProductionDate));
      for (const lot of sortedInventory) {
        if (remainingQuantity <= 0) break;
        const quantityToTake = Math.min(
          remainingQuantity,
          lot.TotalQuantity
        );
        if (quantityToTake > 0) {
          const lotDoseCount = Math.ceil(quantityToTake * packageSize);
          detailsToAdd.push({
            VaccineID: newDetail.VaccineID,
            name: selectedVaccine.VaccineNameTH,
            LotID: lot.LotID,
            RequestQuantity: quantityToTake,
            RequestDate: newDetail.RequestDate || formData.RequestDate,
            DoseCount: lotDoseCount,
          });
          remainingQuantity -= quantityToTake;
        }
      }
      if (remainingQuantity > 0) {
        showNotify("จำนวนเกินคงคลัง", `จำนวนที่ขอเบิก (${requestQuantity}) เกินจำนวนคงคลังทั้งหมด (${requestQuantity - remainingQuantity})`, "warning");
        return;
      }
      setFormData((prevFormData) => ({
        ...prevFormData,
        VaccineDetails: [...prevFormData.VaccineDetails, ...detailsToAdd],
      }));
    }

    setNewDetail({
      VaccineID: "",
      LotID: "",
      RequestQuantity: "",
      RequestDate: formData.RequestDate,
    });
  };

  const handleRemoveDetail = (row) => {
    console.log("Removing detail:", row);
    setFormData({
      ...formData,
      VaccineDetails: formData.VaccineDetails.filter(
        (v) => v.VaccineID !== row.VaccineID || v.LotID !== row.LotID
      ),
    });
  };

  const handleSubmit = async () => {
    console.log("Submitting form:", formData);
    if (!isFormValid()) {
      showNotify("ข้อมูลไม่ครบ", "กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน", "warning");
      return;
    }
    setIsLoading(true);
    try {
      const formattedRequestDate = formData.RequestDate
        ? `${formData.RequestDate.getFullYear()}-${(formData.RequestDate.getMonth() + 1)
            .toString()
            .padStart(2, "0")}-${formData.RequestDate
            .getDate()
            .toString()
            .padStart(2, "0")}`
        : null;
      const response = await apiService({
        method: "POST",
        url: "/createVaccineRequisitions",
        data: { ...formData, RequestDate: formattedRequestDate, VaccineDetails: [] },
        sessionId: sessionId || "",
      });
      console.log("Submit API Response:", response);
      if (response.result === "success") {
        setFormData({ ...formData, TransactionID: response.TransactionID });
        setIsSubmitted(true);
        showNotify("สำเร็จ", "สร้างเอกสารสำเร็จ! เลขที่เอกสาร: " + response.TransactionID, "success");
      }
    } catch (error) {
      console.error("Error creating requisition:", error);
      showNotify("เกิดข้อผิดพลาด", "เกิดข้อผิดพลาดในการสร้างเอกสาร: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const validateQuantities = (details, isApproval = false) => {
    for (const detail of details) {
      // ตรวจสอบจำนวนที่ใช้ (ApprovedQuantity สำหรับการอนุมัติ, RequestQuantity สำหรับการบันทึกปกติ)
      const quantityToCheck = isApproval 
        ? (Number(detail.ApprovedQuantity) || 0)
        : (Number(detail.RequestQuantity) || 0);
      
      // ถ้าจำนวนเป็น 0 ให้ข้ามการตรวจสอบ Lot และจำนวนคงคลัง
      if (quantityToCheck === 0) {
        console.log(`Skipping validation for VaccineID=${detail.VaccineID} because quantity is 0`);
        continue;
      }
      
      // ตรวจสอบ Lot ว่ามีในคลังหรือไม่
      const selectedLot = inventory.find(
        (item) => item.VaccineID === detail.VaccineID && item.LotID === detail.LotID
      );
      if (!selectedLot) {
        showNotify("ไม่พบข้อมูล", `Lot ${detail.LotID} สำหรับวัคซีน ${detail.VaccineID} ไม่พบในคลัง`, "warning");
        return false;
      }
      
      // ตรวจสอบจำนวนว่าคงคลังพอหรือไม่
      if (quantityToCheck > selectedLot.TotalQuantity) {
        const quantityLabel = isApproval ? "จำนวนที่จ่าย" : "จำนวนที่ขอเบิก";
        showNotify("จำนวนเกินคงคลัง", `${quantityLabel} (${quantityToCheck.toLocaleString()}) สำหรับวัคซีน ${detail.name} (Lot ${detail.LotID}) เกินจำนวนคงคลัง (${selectedLot.TotalQuantity.toLocaleString()})`, "warning");
        return false;
      }
    }
    return true;
  };
  
  
  const handleConfirm = async (overrideData = {}, forceApprovedApi = false) => {
    if (overrideData?.target || overrideData?.currentTarget) {
      console.warn("handleConfirm received an event object, using empty object instead");
      overrideData = {};
    }
  
    const mergedFormData = {
      ...formData,
      ...overrideData,
    };
  
    // ตรวจสอบจำนวนคลัง (ส่ง flag isApproval เพื่อบอกว่าเป็นการอนุมัติหรือไม่)
    if (!validateQuantities(mergedFormData.VaccineDetails, forceApprovedApi)) return;
  
    if (!isFormValid()) {
      showNotify("ข้อมูลไม่ครบ", "กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน", "warning");
      return;
    }
  
    const formattedRequestDate = mergedFormData.RequestDate
      ? `${mergedFormData.RequestDate.getFullYear()}-${(mergedFormData.RequestDate.getMonth() + 1)
          .toString()
          .padStart(2, "0")}-${mergedFormData.RequestDate
          .getDate()
          .toString()
          .padStart(2, "0")}`
      : null;
  
    const submitData = {
      ...mergedFormData,
      RequestDate: formattedRequestDate,
      VaccineDetails: mergedFormData.VaccineDetails.map((detail) => ({
        ...detail,
        RequestDate: detail.RequestDate
          ? `${detail.RequestDate.getFullYear()}-${(detail.RequestDate.getMonth() + 1)
              .toString()
              .padStart(2, "0")}-${detail.RequestDate
              .getDate()
              .toString()
              .padStart(2, "0")}`
          : null,
        name: String(detail.name || ""),
      })),
      deliveryAddress: String(mergedFormData.deliveryAddress || ""),
      deliveryBy: String(mergedFormData.deliveryBy || ""),
      deliveryVehicle: String(mergedFormData.deliveryVehicle || ""),
      deliveryStaff: String(mergedFormData.deliveryStaff || ""),
      deliveryRoute: String(mergedFormData.deliveryRoute || ""),
      trackingNumber: String(mergedFormData.trackingNumber || ""),
    };
  
    Object.keys(submitData).forEach((key) => {
      if (submitData[key] === undefined || submitData[key] === null) {
        submitData[key] = "";
      }
    });
  
    setIsLoading(true);
    try {
      const apiEndpoint = forceApprovedApi
        ? "/approvedVaccineRequisitionsByID"
        : "/updateVaccineRequisitionsByID";
  
      const response = await apiService({
        method: "POST",
        url: apiEndpoint,
        data: submitData,
        sessionId: sessionId || "",
      });
  
      console.log("API Response:", response);
      if (response.result === "success") {
        showNotify("สำเร็จ", "บันทึกใบเบิกสำเร็จ!", "success", () => navigate("/vaccinerequisitionrequestlist"));
      }
    } catch (error) {
      console.error("API Error:", error.message);
      showNotify("เกิดข้อผิดพลาด", "เกิดข้อผิดพลาดในการบันทึก: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };
  

  const openApproveDialog = () => {
    setDialogConfig({ isOpen: true, type: "approved" });
  };

  const handleApproved = () => {
    closeDialog();
    const updatedDetails = formData.VaccineDetails.map((detail) => ({
      ...detail,
      ApprovedQuantity: detail.RequestQuantity,
      ConfirmedQuantity: detail.RequestQuantity,
    }));
  
    handleConfirm(
      {
        Status: "Approved",
        VaccineDetails: updatedDetails,
      },
      true // forceApprovedApi = true
    );
  };
  
  const openReturnToDraftDialog = () => {
    if (!formData.TransactionID) {
      showNotify("ไม่พบข้อมูล", "ไม่พบเลขที่ใบเบิก", "warning");
      return;
    }
    setDialogConfig({ isOpen: true, type: "returnToDraft" });
  };

  const handleReturnToDraft = async () => {
    closeDialog();
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/returnVaccineRequisitionsToDraftByID",
        data: {
          TransactionID: formData.TransactionID,
          UpdatedBy: profile?.UserID || profile?.Email || "",
        },
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        setTemperatureFile(null);
        setTemperatureFileUrl("");
        setRemoveTemperatureFile(false);
        setIsEditingFile(false);
        showNotify("สำเร็จ", "ส่งกลับให้ผู้ขอเบิกแก้ไขเรียบร้อยแล้ว!", "success", () => navigate("/vaccinerequisitionrequestlist"));
      } else {
        showNotify(
          "เกิดข้อผิดพลาด",
          "เกิดข้อผิดพลาดในการส่งกลับ: " + (response.error || "ไม่ทราบสาเหตุ"),
          "error"
        );
      }
    } catch (error) {
      console.error("Error returning requisition to draft:", error);
      showNotify("เกิดข้อผิดพลาด", "เกิดข้อผิดพลาดในการส่งกลับ: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const openRevertDialog = () => {
    if (!formData.TransactionID) {
      showNotify("ไม่พบข้อมูล", "ไม่พบเลขที่ใบเบิก", "warning");
      return;
    }
    setDialogConfig({ isOpen: true, type: "revert" });
  };

  const openReconfirmDialog = () => {
    if (!formData.TransactionID) {
      showNotify("ไม่พบข้อมูล", "ไม่พบเลขที่ใบเบิก", "warning");
      return;
    }
    setDialogConfig({ isOpen: true, type: "reconfirm" });
  };

  const closeDialog = () => {
    setDialogConfig({ isOpen: false, type: null });
  };

  const handleRevertApproved = async () => {
    closeDialog();
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/revertApprovedVaccineRequisitionsByID",
        data: {
          TransactionID: formData.TransactionID,
        },
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        showNotify("สำเร็จ", "คืนค่าการจ่ายวัคซีนเรียบร้อยแล้ว\nสามารถอนุมัติจ่ายใหม่ได้", "success", () => navigate("/vaccinerequisitionrequestlist"));
      } else {
        showNotify(
          "เกิดข้อผิดพลาด",
          "เกิดข้อผิดพลาดในการคืนค่า: " + (response.error || "ไม่ทราบสาเหตุ"),
          "error"
        );
      }
    } catch (error) {
      console.error("Error reverting approved requisition:", error);
      showNotify("เกิดข้อผิดพลาด", "เกิดข้อผิดพลาดในการคืนค่า: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReconfirm = async () => {
    closeDialog();
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/reconfirmVaccineRequisitionsByID",
        data: {
          TransactionID: formData.TransactionID,
        },
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        showNotify("สำเร็จ", "ยืนยันการจ่ายวัคซีนตามเดิมเรียบร้อยแล้ว\nรอคลังปลายทางรับเข้าคลัง", "success", () => navigate("/vaccinerequisitionrequestlist"));
      } else {
        showNotify(
          "เกิดข้อผิดพลาด",
          "เกิดข้อผิดพลาด: " + (response.error || "ไม่ทราบสาเหตุ"),
          "error"
        );
      }
    } catch (error) {
      console.error("Error reconfirming requisition:", error);
      showNotify("เกิดข้อผิดพลาด", "เกิดข้อผิดพลาด: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDelivery = async () => {
    if (!formData.TransactionID) {
      showNotify("ไม่พบข้อมูล", "ไม่พบเลขที่ใบเบิก", "warning");
      return;
    }

    if (!formData.deliveryAddress?.trim()) {
      showNotify("ข้อมูลไม่ครบ", "กรุณาระบุที่อยู่จัดส่งวัคซีน", "warning");
      return;
    }

    setIsDeliverySaving(true);
    try {
      const payload = new FormData();
      payload.append("TransactionID", formData.TransactionID);
      payload.append("DeliveryAddress", formData.deliveryAddress || "");
      payload.append("DeliveryBy", formData.deliveryBy || "");
      payload.append("DeliveryVehicle", formData.deliveryVehicle || "");
      payload.append("DeliveryStaff", formData.deliveryStaff || "");
      payload.append("DeliveryRoute", formData.deliveryRoute || "");
      payload.append("TrackingNumber", formData.trackingNumber || "");
      let deliveryDateIso = "";
      if (formData.deliveryDate) {
        const dateValue =
          formData.deliveryDate instanceof Date
            ? formData.deliveryDate
            : new Date(formData.deliveryDate);
        if (!Number.isNaN(dateValue.getTime())) {
          deliveryDateIso = dateValue.toISOString();
        }
      }
      payload.append("DeliveryDate", deliveryDateIso);
      payload.append(
        "RemoveTemperatureFile",
        removeTemperatureFile ? "true" : "false"
      );
      payload.append(
        "UpdatedBy",
        profile?.UserID || profile?.Email || ""
      );

      if (temperatureFile) {
        payload.append("TemperatureFile", temperatureFile);
      }

      const response = await apiService({
        method: "POST",
        url: "/saveVaccineRequisitionDelivery",
        data: payload,
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        const delivery = response.delivery || {};
        const updatedDeliveryDate = delivery.DeliveryDate
          ? delivery.DeliveryDate.split("T")[0]
          : "";

        setFormData((prev) => ({
          ...prev,
          deliveryAddress: delivery.DeliveryAddress || "",
          deliveryBy: delivery.DeliveryBy || "",
          deliveryVehicle: delivery.DeliveryVehicle || "",
          deliveryStaff: delivery.DeliveryStaff || "",
          deliveryRoute: delivery.DeliveryRoute || "",
          trackingNumber: delivery.TrackingNumber || "",
          deliveryDate: updatedDeliveryDate,
        }));
        setTemperatureFile(null);
        setTemperatureFileUrl(delivery.TemperatureFileUrl || "");
        setRemoveTemperatureFile(false);
        setIsEditingFile(false);
        showNotify("สำเร็จ", "บันทึกข้อมูลการจัดส่งวัคซีนสำเร็จ!", "success");
      } else {
        showNotify(
          "เกิดข้อผิดพลาด",
          "เกิดข้อผิดพลาดในการบันทึกข้อมูลการจัดส่งวัคซีน: " + (response.error || "ไม่ทราบสาเหตุ"),
          "error"
        );
      }
    } catch (error) {
      console.error("Error saving delivery data:", error);
      showNotify("เกิดข้อผิดพลาด", "เกิดข้อผิดพลาดในการบันทึก: " + error.message, "error");
    } finally {
      setIsDeliverySaving(false);
    }
  };


  const openRejectedDialog = () => {
    setDialogConfig({ isOpen: true, type: "rejected" });
  };

  const handleRejected = async () => {
    closeDialog();
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/rejectedVaccineRequisitionsByID",
        data: { TransactionID: formData.TransactionID },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        showNotify("สำเร็จ", "ยกเลิกเอกสารใบเบิกสำเร็จ!", "success", () => navigate("/vaccinerequisitionrequestlist"));
      } else {
        showNotify("เกิดข้อผิดพลาด", "เกิดข้อผิดพลาดในการยกเลิก: " + response.error, "error");
      }
    } catch (error) {
      console.error("Error cancelling requisition:", error);
      showNotify("เกิดข้อผิดพลาด", "เกิดข้อผิดพลาดในการยกเลิก: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const isEditable = !isSubmitted;

  // Update columns to pass handleAddLot to LotNoCell
  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faListOl} />
          <span>รหัสวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineID,
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faSyringe} />
          <span>ชื่อวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.name,
      sortable: true,
      width: "350px",
    },
    // ซ่อนคอลัมน์จำนวน (ขวด) สำหรับกรณีที่สถานะไม่ใช่ Pending
    ...(isReadOnly
      ? []
      : [
          {
            name: (
              <HStack>
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>จำนวนที่ขอเบิก (ขวด)</span>
              </HStack>
            ),
            selector: (row) =>
              originalRequestedMap[row.VaccineID] ?? row.RequestQuantity ?? 0,
            sortable: true,
            width: "200px",
            cell: (row) => (
              <Box width="100%" ml={10} color="blue.600" fontWeight="bold">
                {(
                  originalRequestedMap[row.VaccineID] ??
                  row.RequestQuantity ??
                  0
                ).toLocaleString()}
              </Box>
            ),
          },
          {
            name: (
              <HStack>
                <FontAwesomeIcon icon={faCalculator} />
                <span>จำนวนที่จ่าย (ขวด)</span>
              </HStack>
            ),
            selector: (row) => row.RequestQuantity,
            sortable: true,
            width: "200px",
            cell: (row) => (
              <Input
                type="number"
                value={row.RequestQuantity}
                onChange={(e) =>
                  handleQuantityChange(
                    row.VaccineID,
                    row.LotID,
                    e.target.value
                  )
                }
                min={0}
                size="sm"
                width="100px"
                ml={10}
              />
            ),
          },
        ]),
    // เพิ่มคอลัมน์สำหรับกรณีที่สถานะไม่ใช่ Pending
    ...(isReadOnly ? [
      {
        name: (
          <HStack>
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>จำนวนที่ขอเบิก (ขวด)</span>
          </HStack>
        ),
        selector: (row) => row.RequestQuantity,
        sortable: true,
        width: "200px",
        cell: (row) => (
          <Box width="100%" ml={10} color="blue.600" fontWeight="bold" textAlign="right">
            {row.RequestQuantity.toLocaleString()}
          </Box>
        ),
      },
      {
        name: (
          <HStack>
            <FontAwesomeIcon icon={faCheckDouble} />
            <span>จำนวนที่จ่าย (ขวด)</span>
          </HStack>
        ),
        selector: (row) => row.ApprovedQuantity || 0,
        sortable: true,
        width: "200px",
        cell: (row) => (
          <Box width="100%" ml={10} color="green.600" fontWeight="bold" textAlign="right">
            {(row.ApprovedQuantity || 0).toLocaleString()}
          </Box>
        ),
      },
      {
        name: (
          <HStack>
            <FontAwesomeIcon icon={faCapsules} />
            <span>จำนวนที่จ่าย (โดส)</span>
          </HStack>
        ),
        selector: (row) =>
          (row.ApprovedQuantity || 0) *
          (packageSizeMap[row.VaccineID] || 1),
        sortable: true,
        width: "200px",
        cell: (row) => {
          const packageSize = packageSizeMap[row.VaccineID] || 1;
          const approvedDoses = (row.ApprovedQuantity || 0) * packageSize;
          return (
            <Box width="100%" ml={10} color="purple.600" fontWeight="bold" textAlign="right">
              {approvedDoses.toLocaleString()}
            </Box>
          );
        },
      },
    ] : []),
    // ซ่อนคอลัมน์จำนวนโดสสำหรับกรณีที่สถานะไม่ใช่ Pending
    ...(isReadOnly ? [] : [{
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCapsules} />
          <span>จำนวนที่จ่าย (โดส)</span>
        </HStack>
      ),
      selector: (row) => row.DoseCount,
      sortable: true,
      width: "200px",
      cell: (row) => (
        <Box width="100%" ml={10}>
          {row.DoseCount.toLocaleString()}
        </Box>
      ),
    }]),
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTag} />
          <span>Lot No.</span>
        </HStack>
      ),
      selector: (row) => row.LotID,
      sortable: true,
      cell: (row) => (
        isReadOnly ? (
          <Box width="100%" ml={10}>
            {row.LotID || "-"}
          </Box>
        ) : (
          isInventoryLoading ? (
            <Spinner size="sm" ml={10} />
          ) : (
          <LotNoCell
            row={row}
            inventory={inventory}
            handleLotChange={handleLotChange}
            handleAddLot={handleAddLot}
            formData={formData}
          />
          )
        )
      ),
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faGears} />
          <span>การดำเนินการ</span>
        </HStack>
      ),
      cell: (row) => (
        <HStack spacing={2}>
          {!isReadOnly && (
            <IconButton.DeleteIcon
              onClick={() => handleRemoveDetail(row)}
              size="sm"
            />
          )}
          {isApprovedStatus && (
            <Button
              leftIcon={<FontAwesomeIcon icon={faTag} />}
              size="sm"
              colorScheme="green"
              onClick={() => printPackingLabel(row)}
            >
              พิมพ์ Packing Label
            </Button>
          )}
        </HStack>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "250px",
    },
  ];

  if (!sessionId) {
    console.log("No sessionId, rendering null");
    return null;
  }

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <PrintableRequisitionForm
        formData={formData}
        parentWarehouses={parentWarehouses}
        warehouses={warehouses}
        vaccineOptions={vaccineOptions}
      />
      <StandardPageTitle title="รายการเบิกวัคซีน" />

      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <StandardHeader
          title="ข้อมูลเอกสารการเบิกวัคซีน"
          bgColor="iBlue.600"
          icon={faFileLines}
        />
        <Grid templateColumns="repeat(4, 1fr)" gap={6} p={5}>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                เลขที่เอกสาร
              </FormLabel>
              <Input
                name="TransactionID"
                value={formData.TransactionID}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                วันที่ขอเบิก
              </FormLabel>
              <CustomDatePicker
                name="RequestDate"
                value={formData.RequestDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                isDisabled={isReadOnly}
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl
              isRequired
              isInvalid={!!errors.FromWarehouseID}
              isDisabled={isSubmitted}
            >
              <FormLabel fontSize="xl" color="gray">
                คลังต้นทาง
              </FormLabel>
              <Select
                name="FromWarehouseID"
                value={formData.FromWarehouseID}
                onChange={handleInputChange}
                placeholder="เลือกคลังต้นทาง"
                colorScheme="iBlue"
                isDisabled={isReadOnly}
              >
                {console.log("Rendering FromWarehouseID dropdown with parentWarehouses:", parentWarehouses)}
                {parentWarehouses.length > 0 ? (
                  parentWarehouses.map((warehouse) => (
                    <option
                      key={warehouse.WarehouseID}
                      value={warehouse.WarehouseID}
                    >
                      {warehouse.WarehouseName}
                    </option>
                  ))
                ) : (
                  <option disabled>ไม่มีข้อมูลคลังต้นทาง</option>
                )}
              </Select>
              <FormErrorMessage>{errors.FromWarehouseID}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                คลังปลายทาง
              </FormLabel>
              <Select
                name="ToWarehouseID"
                value={formData.ToWarehouseID}
                onChange={handleInputChange}
                placeholder="เลือกคลังปลายทาง (ถ้ามี)"
                colorScheme="iBlue"
                isDisabled={isSubmitted || isReadOnly}
              >
                {console.log("Rendering ToWarehouseID dropdown with warehouses:", warehouses)}
                {warehouses.length > 0 ? (
                  warehouses.map((warehouse) => (
                    <option
                      key={warehouse.WarehouseID}
                      value={warehouse.WarehouseID}
                    >
                      {warehouse.WarehouseName}
                    </option>
                  ))
                ) : (
                  <option disabled>ไม่มีข้อมูลคลังปลายทาง</option>
                )}
              </Select>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                ประเภทงบประมาณ
              </FormLabel>
              <Select
                name="BudgetType"
                value={formData.BudgetType}
                onChange={handleInputChange}
                placeholder="เลือกประเภทงบประมาณ"
                colorScheme="iBlue"
                isDisabled={isSubmitted || isReadOnly}
              >
                <option value="Prevention">งบป้องกัน</option>
                <option value="Distribution">งบจำหน่าย</option>
              </Select>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                ประเภทการเบิก
              </FormLabel>
              <Input
                name="RequestType"
                value={formData.RequestType}
                onChange={handleInputChange}
                colorScheme="iBlue"
                isReadOnly={isReadOnly}
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                ประเภทเอกสารอ้างอิง
              </FormLabel>
              <Input
                name="ReferenceDocumentType"
                value={formData.ReferenceDocumentType}
                onChange={handleInputChange}
                colorScheme="iBlue"
                isReadOnly={isReadOnly}
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                รอบการเบิก
              </FormLabel>
              <Input
                name="ReferenceDocumentNo"
                value={formData.ReferenceDocumentNo}
                onChange={handleInputChange}
                colorScheme="iBlue"
                isReadOnly={isReadOnly}
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                เจ้าหน้าที่คลัง
              </FormLabel>
              <Input
                name="WarehouseStaffID"
                value={formData.WarehouseStaffID}
                onChange={handleInputChange}
                colorScheme="iBlue"
                isReadOnly={isReadOnly}
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={4}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                รายละเอียดเพิ่มเติม
              </FormLabel>
              <Textarea
                name="RequestDetail"
                value={formData.RequestDetail}
                onChange={handleInputChange}
                colorScheme="iBlue"
                isReadOnly={isReadOnly}
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={4}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                สถานะ
              </FormLabel>
              <Input
                name="Status"
                value={getStatusText(formData.Status)}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
        </Grid>
        {!isSubmitted && (
          <Box p={5} textAlign="right">
            <HStack spacing={5} mb={5} textAlign="right">
              <IconButton.Cancel
                onClick={() => navigate("/vaccinerequisitionrequestlist")}
              />
              <IconButton.Submit label="สร้างเอกสาร" onClick={handleSubmit} />
            </HStack>
          </Box>
        )}
      </Box>

      {isSubmitted && (
        <>
          <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
          title="รายการวัคซีนที่เบิก"
          bgColor="iOrange.500"
          icon={faFileLines}
        />
            <Box p={5} overflowX="auto">
              <DataTable
                columns={columns}
                data={formData.VaccineDetails}
                pagination
                highlightOnHover
                striped
                responsive
                customStyles={customStyles}
              />
            </Box>
          </Box>

          {/* สรุปข้อมูลวัคซีนที่จ่าย */}
          <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
          title="สรุปข้อมูลวัคซีนที่จ่าย"
          bgColor="iPurple.500"
          icon={faCalculator}
        />

            
            <Box p={5}>
              {/* สรุปแยกตามชนิดวัคซีน */}
              <Box mb={6}>
                <Heading size="md" mb={4} color="gray.700">
                  สรุปแยกตามชนิดวัคซีน
                </Heading>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>รหัสวัคซีน</Th>
                      <Th>ชื่อวัคซีน</Th>
                      <Th textAlign="right">จำนวนที่ขอเบิก</Th>
                      <Th textAlign="right">จำนวนที่จ่าย</Th>
                      <Th textAlign="right">ส่วนต่าง</Th>
                      <Th textAlign="right">จำนวน Lot</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(() => {
                      // จัดกลุ่มข้อมูลตาม VaccineID
                      const vaccineGroups = formData.VaccineDetails.reduce((groups, detail) => {
                        const key = detail.VaccineID;
                        if (!groups[key]) {
                          groups[key] = {
                            VaccineID: detail.VaccineID,
                            name: detail.name,
                            requestedBottles: 0, // จำนวนที่ขอเบิก (จากต้นฉบับ)
                            totalBottles: 0,      // จำนวนที่จ่าย
                            totalDoses: 0,
                            lotCount: 0
                          };
                        }
                        const approvedQuantity = isReadOnly
                          ? detail.ApprovedQuantity || 0
                          : detail.RequestQuantity || 0;
                        const packageSize =
                          packageSizeMap[detail.VaccineID] || 1;

                        groups[key].totalBottles += approvedQuantity;
                        groups[key].totalDoses +=
                          approvedQuantity * packageSize;
                        groups[key].lotCount += 1;
                        return groups;
                      }, {});
                      
                      // หาจำนวนต้นฉบับจาก originalVaccineDetails
                      Object.keys(vaccineGroups).forEach(vaccineId => {
                        vaccineGroups[vaccineId].requestedBottles =
                          originalRequestedMap[vaccineId] || 0;
                      });
                      
                      return Object.values(vaccineGroups).map((group, index) => {
                        const difference = group.totalBottles - group.requestedBottles;
                        const differenceText = difference === 0 ? "0" : 
                          difference > 0 ? `+${difference.toLocaleString()}` : 
                          difference.toLocaleString();
                        const differenceColor = difference === 0 ? "gray.500" : 
                          difference > 0 ? "green.500" : "red.500";
                        
                        return (
                          <Tr key={index}>
                            <Td>{group.VaccineID}</Td>
                            <Td>{group.name}</Td>
                            <Td textAlign="right">{group.requestedBottles.toLocaleString()}</Td>
                            <Td textAlign="right">{group.totalBottles.toLocaleString()}</Td>
                            <Td textAlign="right" color={differenceColor} fontWeight="bold">
                              {differenceText}
                            </Td>
                            <Td textAlign="right">{group.lotCount}</Td>
                          </Tr>
                        );
                      });
                    })()}
                  </Tbody>
                </Table>
              </Box>

              {/* สรุปจำนวนรวมทั้งหมด */}
              <Box mb={6}>
                <Heading size="md" mb={4} color="gray.700">
                  สรุปจำนวนรวมทั้งหมด
                </Heading>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <Box p={4} borderWidth={1} borderRadius="md" bgColor="blue.50">
                    <Text fontSize="lg" fontWeight="bold" color="blue.600">
                      ยอดรวมจำนวนที่ขอเบิก
                    </Text>
                    <Text fontSize="xl" fontWeight="bold" color="blue.800">
                      {requestedTotals.bottles.toLocaleString()} ขวด / {requestedTotals.doses.toLocaleString()} โดส
                    </Text>
                  </Box>
                  <Box p={4} borderWidth={1} borderRadius="md" bgColor="green.50">
                    <Text fontSize="lg" fontWeight="bold" color="green.600">
                      ยอดรวมจำนวนที่อนุมัติจ่าย
                    </Text>
                    <Text fontSize="xl" fontWeight="bold" color="green.800">
                      {approvedTotals.bottles.toLocaleString()} ขวด   /  {approvedTotals.doses.toLocaleString()} โดส
                    </Text>
                  </Box>
                </Grid>
              </Box>

              {/* สรุปแยกตาม Lot */}
              <Box>
                <Heading size="md" mb={4} color="gray.700">
                  สรุปแยกตาม Lot
                </Heading>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>รหัสวัคซีน</Th>
                      <Th>ชื่อวัคซีน</Th>
                      <Th>Lot No.</Th>
                      <Th textAlign="right">จำนวนขวด</Th>
                      <Th textAlign="right">จำนวนโดส</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {formData.VaccineDetails.map((detail, index) => {
                      const quantityForSummary = isReadOnly
                        ? detail.ApprovedQuantity ?? detail.RequestQuantity ?? 0
                        : detail.RequestQuantity ?? 0;
                      const packageSize =
                        packageSizeMap[detail.VaccineID] ||
                        detail.packageSize ||
                        1;
                      const dosesForSummary = Math.ceil(
                        quantityForSummary * packageSize
                      );
                      return (
                        <Tr key={index}>
                          <Td>{detail.VaccineID}</Td>
                          <Td>{detail.name}</Td>
                          <Td>{detail.LotID}</Td>
                          <Td textAlign="right">
                            {quantityForSummary.toLocaleString()}
                          </Td>
                          <Td textAlign="right">
                            {dosesForSummary.toLocaleString()}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          </Box>

          <Box p={5} textAlign="right">
            {formData.Status === "Pending" && (
              <HStack spacing={50} mb={5}>
              <IconButton.Reject onClick={openRejectedDialog} isLoading={isLoading} label="ยกเลิก / ไม่อนุมัติ" />
                <IconButton.Send
                  onClick={openReturnToDraftDialog}
                  isLoading={isLoading}
                  colorScheme="iOrange"
                  label="ส่งกลับผู้ขอเบิก (แก้ไข)"
                />
                <IconButton.Approved onClick={openApproveDialog} isLoading={isLoading} />
              </HStack>
            )}
            {formData.Status === "ReceiveRejected" && (
              <HStack spacing={5} mb={5}>
                <Button
                  leftIcon={<FontAwesomeIcon icon={faArrowRotateBackward} />}
                  colorScheme="clearScheme"
                  onClick={openRevertDialog}
                  isLoading={isLoading}
                  minW="max-content"
                >
                  ยกเลิกการจ่ายวัคซีน (เพื่ออนุมัติจ่ายใหม่)
                </Button>
                <Button
                  leftIcon={<FontAwesomeIcon icon={faCircleCheck} />}
                  colorScheme="confirmScheme"
                  onClick={openReconfirmDialog}
                  isLoading={isLoading}
                  minW="max-content"
                >
                  ยืนยันการจ่ายวัคซีนตามเดิม (รอรับเข้าคลัง)
                </Button>
              </HStack>
            )}
          </Box>

          {isApprovedStatus && (
            <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
              <StandardHeader
                title="การจัดส่งวัคซีน"
                bgColor="iGreen.500"
                icon={faFileLines}
              />
              <Box p={5} overflowX="auto">
                <Grid templateColumns="repeat(3, 1fr)" gap={6} p={5}>
                  <GridItem colSpan={3}>
                    <FormControl>
                      <FormLabel fontSize="xl" color="gray">
                        ที่อยู่จัดส่งวัคซีน
                      </FormLabel>
                      <Input
                        name="deliveryAddress"
                        value={formData.deliveryAddress}
                        onChange={handleInputChange}
                        colorScheme="iGreen"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={1}>
                    <FormControl>
                      <FormLabel fontSize="xl" color="gray">
                        ทะเบียนรถขนส่ง
                      </FormLabel>
                      <Input
                        name="deliveryVehicle"
                        value={formData.deliveryVehicle}
                        onChange={handleInputChange}
                        colorScheme="iGreen"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={1}>
                    <FormControl>
                      <FormLabel fontSize="xl" color="gray">
                        จัดส่งโดย / พนักงานขับรถ
                      </FormLabel>
                      <Input
                        name="deliveryBy"
                        value={formData.deliveryBy}
                        onChange={handleInputChange}
                        colorScheme="iGreen"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={1}>
                    <FormControl>
                      <FormLabel fontSize="xl" color="gray">
                        เจ้าหน้าที่ผู้ดูแลการจัดส่ง
                      </FormLabel>
                      <Input
                        name="deliveryStaff"
                        value={formData.deliveryStaff}
                        onChange={handleInputChange}
                        colorScheme="iGreen"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={1}>
                    <FormControl>
                      <FormLabel fontSize="xl" color="gray">
                        เส้นทางจัดส่ง
                      </FormLabel>
                      <Input
                        name="deliveryRoute"
                        value={formData.deliveryRoute}
                        onChange={handleInputChange}
                        colorScheme="iGreen"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={1}>
                    <FormControl>
                      <FormLabel fontSize="xl" color="gray">
                        หมายเลขติดตาม
                      </FormLabel>
                      <Input
                        name="trackingNumber"
                        value={formData.trackingNumber}
                        onChange={handleInputChange}
                        colorScheme="iGreen"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={1}>
                    <FormControl>
                      <FormLabel fontSize="xl" color="gray">
                        วันที่จัดส่ง
                      </FormLabel>
                      <CustomDatePicker
                        value={formData.deliveryDate}
                        onChange={(event) =>
                          setFormData({
                            ...formData,
                            deliveryDate: event.target.value || "",
                          })
                        }
                        isClearable
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={3}>
                    <FormControl>
                      <FormLabel fontSize="xl" color="gray">
                        ไฟล์อุณหภูมิการขนส่งวัคซีน
                      </FormLabel>
                      {temperatureFileUrl && !isEditingFile && !temperatureFile ? (
                        <HStack spacing={4} mt={2}>
                          <Link
                            href={normalizedTemperatureFileUrl}
                            color="iGreen.600"
                            fontWeight="bold"
                            isExternal
                          >
                            ดูไฟล์ที่แนบไว้
                          </Link>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={handleEditExistingFile}
                            leftIcon={<FontAwesomeIcon icon={faEdit} />}
                          >
                            เปลี่ยนไฟล์
                          </Button>
                          <Button
                            size="sm"
                            colorScheme="red"
                            onClick={handleRemoveExistingFile}
                            leftIcon={<FontAwesomeIcon icon={faTrash} />}
                          >
                            ลบไฟล์
                          </Button>
                        </HStack>
                      ) : (
                        <Box>
                          <Box
                            {...getRootProps()}
                            border="2px dashed"
                            borderColor={isDragActive ? "green.500" : "gray.300"}
                            borderRadius="md"
                            p={4}
                            textAlign="center"
                            cursor="pointer"
                            bg={isDragActive ? "green.50" : "transparent"}
                          >
                            <input {...getInputProps()} />
                            <FontAwesomeIcon icon={faFileUpload} size="2x" color="gray" />
                            <Box mt={2}>
                              {isDragActive
                                ? "ปล่อยไฟล์ที่นี่ ..."
                                : "ลากและวางไฟล์ หรือคลิกเพื่อเลือก (JPEG, PNG, PDF, XLSX, XLS)"}
                            </Box>
                          </Box>
                          {temperatureFile && (
                            <HStack spacing={3} mt={2}>
                              <Text color="gray.600">{temperatureFile.name}</Text>
                              <Button
                                size="sm"
                                colorScheme="red"
                                onClick={handleRemoveNewFile}
                              >
                                ลบไฟล์
                              </Button>
                              {temperatureFileUrl && (
                                <Button
                                  size="sm"
                                  onClick={handleCancelEditFile}
                                >
                                  ยกเลิก
                                </Button>
                              )}
                            </HStack>
                          )}
                          {!temperatureFile && temperatureFileUrl && (
                            <HStack spacing={3} mt={2}>
                              <Link
                                href={normalizedTemperatureFileUrl}
                                color="iGreen.600"
                                isExternal
                              >
                                ดูไฟล์เดิม
                              </Link>
                              <Button size="sm" onClick={handleCancelEditFile}>
                                ยกเลิก
                              </Button>
                            </HStack>
                          )}
                        </Box>
                      )}
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>
            </Box>
          )}

          <Box p={5} textAlign="right">
            <HStack spacing={50} mb={5}>
              <IconButton.Back
                onClick={() => navigate("/vaccinerequisitionrequestlist")}
                label="ย้อนกลับ"
              />
              <Button
                leftIcon={<FontAwesomeIcon icon={faPrint} />}
                colorScheme="blue"
                onClick={() => {
                  console.log("Print Requisition button clicked. FormData:", formData);
                  printTable();
                }}
              >
                พิมพ์ใบเบิก
              </Button>
              <Button
                leftIcon={<FontAwesomeIcon icon={faPrint} />}
                colorScheme="teal"
                isDisabled={!isApprovedStatus}
                onClick={() => {
                  console.log("Print Delivery Note button clicked. FormData:", formData);
                  printDeliveryNote();
                }}
              >
                พิมพ์ใบนำส่ง
              </Button>
              <Button
                leftIcon={<FontAwesomeIcon icon={faPrint} />}
                colorScheme="purple"
                isDisabled={!isApprovedStatus}
                onClick={() => {
                  console.log("Print Inspection Note button clicked. FormData:", formData);
                  printInspectionNote();
                }}
              >
                พิมพ์ใบตรวจนับ
              </Button>
              {isApprovedStatus && (
                <IconButton.Save
                  onClick={handleConfirmDelivery}
                  isLoading={isDeliverySaving}
                  label="บันทึกการจัดส่งวัคซีน"
                />
              )}
            </HStack>
          </Box>
        </>
      )}

      {/* AlertDialog สำหรับคืนค่าการจ่าย / ยืนยันการจ่ายตามเดิม */}
      <AlertDialog
        isOpen={dialogConfig.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeDialog}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent maxW="lg">
            {dialogConfig.type === "revert" && (
              <>
                <AlertDialogHeader fontSize="xl" fontWeight="bold" color="orange.600" borderBottomWidth={1} pb={3}>
                  ยกเลิกการจ่ายวัคซีน
                </AlertDialogHeader>
                <AlertDialogBody py={5}>
                  <Box mb={3}>
                    <Text fontWeight="bold" mb={2}>เลขที่ใบเบิก: {formData.TransactionID}</Text>
                  </Box>
                  <Box bg="orange.50" p={4} borderRadius="md" borderLeft="4px solid" borderColor="orange.400" mb={3}>
                    <Text fontWeight="bold" color="orange.700" mb={2}>
                      ระบบจะดำเนินการดังนี้:
                    </Text>
                    <UnorderedList color="orange.700" fontSize="sm" spacing={1}>
                      <ListItem>คืนค่าคลังวัคซีน (Quantity / Reserved)</ListItem>
                      <ListItem>ลบรายการ Lot ที่เพิ่มตอนอนุมัติ</ListItem>
                      <ListItem>รีเซ็ตข้อมูลการอนุมัติจ่าย</ListItem>
                      <ListItem>เปลี่ยนสถานะกลับเป็น "รออนุมัติ"</ListItem>
                    </UnorderedList>
                  </Box>
                  <Text fontSize="sm" color="gray.500">
                    หลังจากดำเนินการ สามารถอนุมัติจ่ายวัคซีนใหม่ได้
                  </Text>
                </AlertDialogBody>
                <AlertDialogFooter borderTopWidth={1} pt={3}>
                  <Button ref={cancelRef} onClick={closeDialog}>
                    ยกเลิก
                  </Button>
                  <Button colorScheme="orange" onClick={handleRevertApproved} ml={3} isLoading={isLoading}>
                    ยืนยันการยกเลิกการจ่าย
                  </Button>
                </AlertDialogFooter>
              </>
            )}
            {dialogConfig.type === "reconfirm" && (
              <>
                <AlertDialogHeader fontSize="xl" fontWeight="bold" color="green.600" borderBottomWidth={1} pb={3}>
                  ยืนยันการจ่ายวัคซีนตามเดิม
                </AlertDialogHeader>
                <AlertDialogBody py={5}>
                  <Box mb={3}>
                    <Text fontWeight="bold" mb={2}>เลขที่ใบเบิก: {formData.TransactionID}</Text>
                  </Box>
                  <Box bg="green.50" p={4} borderRadius="md" borderLeft="4px solid" borderColor="green.400">
                    <Text color="green.700" fontSize="sm">
                      ระบบจะเปลี่ยนสถานะกลับเป็น "รอรับเข้าคลัง" โดยไม่แก้ไขรายการวัคซีนที่จ่าย
                    </Text>
                    <Text color="green.700" fontSize="sm" mt={2}>
                      คลังปลายทางจะสามารถยืนยันการรับเข้าคลังได้อีกครั้ง
                    </Text>
                  </Box>
                </AlertDialogBody>
                <AlertDialogFooter borderTopWidth={1} pt={3}>
                  <Button ref={cancelRef} onClick={closeDialog}>
                    ยกเลิก
                  </Button>
                  <Button colorScheme="green" onClick={handleReconfirm} ml={3} isLoading={isLoading}>
                    ยืนยันการจ่ายตามเดิม
                  </Button>
                </AlertDialogFooter>
              </>
            )}
            {dialogConfig.type === "approved" && (
              <>
                <AlertDialogHeader fontSize="xl" fontWeight="bold" color="green.600" borderBottomWidth={1} pb={3}>
                  ยืนยันอนุมัติการจ่ายวัคซีน
                </AlertDialogHeader>
                <AlertDialogBody py={5}>
                  <Box mb={3}>
                    <Text fontWeight="bold" mb={2}>เลขที่ใบเบิก: {formData.TransactionID}</Text>
                  </Box>
                  <Box bg="green.50" p={4} borderRadius="md" borderLeft="4px solid" borderColor="green.400">
                    <Text color="green.700" fontSize="sm">
                      คุณแน่ใจหรือไม่ว่าต้องการอนุมัติการจ่ายวัคซีนรายการนี้?
                    </Text>
                  </Box>
                </AlertDialogBody>
                <AlertDialogFooter borderTopWidth={1} pt={3}>
                  <Button ref={cancelRef} onClick={closeDialog}>
                    ยกเลิก
                  </Button>
                  <Button colorScheme="green" onClick={handleApproved} ml={3} isLoading={isLoading}>
                    ยืนยันอนุมัติ
                  </Button>
                </AlertDialogFooter>
              </>
            )}
            {dialogConfig.type === "returnToDraft" && (
              <>
                <AlertDialogHeader fontSize="xl" fontWeight="bold" color="orange.600" borderBottomWidth={1} pb={3}>
                  ส่งกลับให้ผู้ขอเบิกแก้ไข
                </AlertDialogHeader>
                <AlertDialogBody py={5}>
                  <Box mb={3}>
                    <Text fontWeight="bold" mb={2}>เลขที่ใบเบิก: {formData.TransactionID}</Text>
                  </Box>
                  <Box bg="orange.50" p={4} borderRadius="md" borderLeft="4px solid" borderColor="orange.400">
                    <Text color="orange.700" fontSize="sm">
                      คุณต้องการส่งกลับให้ผู้ขอเบิกแก้ไขรายการนี้หรือไม่?
                    </Text>
                  </Box>
                </AlertDialogBody>
                <AlertDialogFooter borderTopWidth={1} pt={3}>
                  <Button ref={cancelRef} onClick={closeDialog}>
                    ยกเลิก
                  </Button>
                  <Button colorScheme="orange" onClick={handleReturnToDraft} ml={3} isLoading={isLoading}>
                    ยืนยันส่งกลับ
                  </Button>
                </AlertDialogFooter>
              </>
            )}
            {dialogConfig.type === "rejected" && (
              <>
                <AlertDialogHeader fontSize="xl" fontWeight="bold" color="red.600" borderBottomWidth={1} pb={3}>
                  ยกเลิกเอกสารใบเบิก
                </AlertDialogHeader>
                <AlertDialogBody py={5}>
                  <Box mb={3}>
                    <Text fontWeight="bold" mb={2}>เลขที่ใบเบิก: {formData.TransactionID}</Text>
                  </Box>
                  <Box bg="red.50" p={4} borderRadius="md" borderLeft="4px solid" borderColor="red.400">
                    <Text color="red.700" fontSize="sm">
                      คุณแน่ใจหรือไม่ว่าต้องการยกเลิกเอกสารใบเบิกรายการนี้?
                    </Text>
                  </Box>
                </AlertDialogBody>
                <AlertDialogFooter borderTopWidth={1} pt={3}>
                  <Button ref={cancelRef} onClick={closeDialog}>
                    ยกเลิก
                  </Button>
                  <Button colorScheme="red" onClick={handleRejected} ml={3} isLoading={isLoading}>
                    ยืนยันยกเลิก
                  </Button>
                </AlertDialogFooter>
              </>
            )}
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Notification Dialog - แทน alert() */}
      <AlertDialog
        isOpen={notifyDialog.isOpen}
        leastDestructiveRef={notifyRef}
        onClose={closeNotify}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent maxW="md">
            <AlertDialogHeader
              fontSize="xl"
              fontWeight="bold"
              borderBottomWidth={1}
              pb={3}
              color={
                notifyDialog.status === "success" ? "green.600" :
                notifyDialog.status === "error" ? "red.600" :
                notifyDialog.status === "warning" ? "orange.600" : "blue.600"
              }
            >
              {notifyDialog.title}
            </AlertDialogHeader>
            <AlertDialogBody py={5}>
              <Box
                bg={
                  notifyDialog.status === "success" ? "green.50" :
                  notifyDialog.status === "error" ? "red.50" :
                  notifyDialog.status === "warning" ? "orange.50" : "blue.50"
                }
                p={4}
                borderRadius="md"
                borderLeft="4px solid"
                borderColor={
                  notifyDialog.status === "success" ? "green.400" :
                  notifyDialog.status === "error" ? "red.400" :
                  notifyDialog.status === "warning" ? "orange.400" : "blue.400"
                }
              >
                <Text
                  color={
                    notifyDialog.status === "success" ? "green.700" :
                    notifyDialog.status === "error" ? "red.700" :
                    notifyDialog.status === "warning" ? "orange.700" : "blue.700"
                  }
                  whiteSpace="pre-line"
                >
                  {notifyDialog.message}
                </Text>
              </Box>
            </AlertDialogBody>
            <AlertDialogFooter borderTopWidth={1} pt={3}>
              <Button
                ref={notifyRef}
                colorScheme={
                  notifyDialog.status === "success" ? "green" :
                  notifyDialog.status === "error" ? "red" :
                  notifyDialog.status === "warning" ? "orange" : "blue"
                }
                onClick={closeNotify}
              >
                ตกลง
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default VaccineRequisitionForm;